/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// app.js — renderer entry point: mounts the app shell.
//
// The shell is a header bar (brand + empty toolbar slot) above the infinite
// desk (DeskView: pan/zoom camera + dot grid, Feature 10). The saved viewport
// is loaded BEFORE DeskView mounts so the restored camera paints first —
// no flash of the default view. Boards arrive with Features 20–30.

import { el } from "./dom.js";
import * as i18n from "./i18n.js";
import { t } from "./i18n.js";
import {
  applyFontSize,
  scaleStepForEvent,
  normalizeFontSize,
  stepFontSize,
  DEFAULT_FONT_SIZE,
} from "./font-scale.js";
import { DeskView } from "./components/desk-view.js";
import { ZoomControl } from "./components/zoom-control.js";
import { DeskLock } from "./components/desk-lock.js";
import { NoticeBanner } from "./components/notice-banner.js";
import { pendingNotices } from "./model/notices.js";
import { DeskController } from "./components/desk-controller.js";
import { SchematicView } from "./components/schematic-view.js";
import { Desk3DView } from "./components/desk-3d-view.js";
import { PalettePanel } from "./components/palette-panel.js";
import { ProjectTabs } from "./components/project-tabs.js";
import {
  ProjectWorkspace,
  WEB_AUTO_SAVE_MS,
} from "./components/project-workspace.js";
import { BuildGuide } from "./components/build-guide.js";
import { ScopeView } from "./components/scope-view.js";
import { AiPanel } from "./components/ai-panel.js";
import { checkConnection, effectiveProvider } from "./ai/connection.js";
import { SPEEDS } from "./components/sim-controller.js";
import { SimHost } from "./components/sim-host.js";
import { SpeakerAudio } from "./components/speaker-audio.js";
import { normalizeSpiceConfig } from "./sim/spice/config.js";
import { NetlistCache } from "./components/netlist-cache.js";
import { MemoryBridge } from "./components/memory-bridge.js";
import { ChipDebugger } from "./components/chip-debugger.js";
import { ChipDesignBridge } from "./components/chip-design-bridge.js";
import { CpuMonitorBridge } from "./components/cpu-monitor-bridge.js";
import { CustomPinoutSync } from "./components/custom-pinout-sync.js";
import { customPinoutOf } from "./model/custom-chip.js";
import { NotificationStack } from "./components/notification-stack.js";
import { NetNameMonitor } from "./components/net-name-monitor.js";
import { AcWiringMonitor } from "./components/ac-wiring-monitor.js";
import { UpdaterMonitor } from "./components/updater-monitor.js";
import { createWireColorDot } from "./components/wire-color-dot.js";
import { createBusWidthBadge } from "./components/bus-width-badge.js";
import { watchHeaderFit } from "./components/header-fit.js";
import { PopupManager } from "./popup-manager.js";
import { AboutDialog } from "./components/about-dialog.js";
import { SettingsDialog } from "./components/settings-dialog.js";
import { bookSvg } from "./components/protocol-doc-button.js";
import { isWeb } from "./web-flavour.js";
import { KeyboardShortcutsDialog } from "./components/keyboard-shortcuts.js";
import { DeskDoc } from "./model/desk-doc.js";
import { MAX_SIGNALS } from "./model/signals.js";
import { createSignalKeys } from "./model/signal-keys.js";
import { SignalRail } from "./components/signal-rail.js";
import { createIntegrationShell } from "./components/integration-shell.js";
import { DesktopExporter } from "./components/desktop-exporter.js";
import { knownConnections } from "./model/serial-connections.js";
import { partTitle } from "./catalog/labels.js";
import { adapterParamsFor, mainsParamsFor } from "./catalog/ac-parts.js";
import { MAX_METERS } from "./catalog/meter.js";
import {
  chipMarking,
  datasheetCrop,
  familiesUsed,
  partDef,
} from "./catalog/index.js";

/** How long after the last camera change to persist the viewport. */
const VIEWPORT_SAVE_DEBOUNCE_MS = 500;

/** Speed-selector labels (keyed by the SimController multiplier). */
const SPEED_LABELS = { 0.25: "×¼", 1: "×1", 4: "×4" };

/**
 * The speed button's face: the speed asked for, and — while the desk cannot
 * keep up with it (sim-state's `behind`, the speed achieved) — that too, in
 * amber, so a run going slower than asked always says so.
 */
function showSpeed(btn, speed, behind) {
  const asked = SPEED_LABELS[speed] ?? `×${speed}`;
  let text = asked;
  let title = t("toolbar.transport.speedTitle");
  if (behind != null) {
    const achieved = `×${i18n.formatNumber(behind, {
      maximumFractionDigits: behind < 1 ? 2 : 1,
    })}`;
    text = `${asked} · ${achieved}`;
    title = t("toolbar.transport.speedBehindTitle", { achieved, asked });
  }
  // It hears every sim-state: write only what changed, or the toolbar is
  // restyled every frame of a run.
  if (btn.textContent !== text) btn.textContent = text;
  if (btn.title !== title) btn.title = title;
  btn.classList.toggle("toolbar-pill-btn--behind", behind != null);
}

/**
 * A transport segment's face: its glyph, then its word in a span of its own —
 * the span a squeezed header hides (`app-header--glyphs`, header-fit.js), the
 * word staying the button's accessible name. One inline run, so it reads
 * exactly as the plain "▶ Run" it replaces, and textContent is still that.
 */
function setTransportFace(btn, glyph, word) {
  btn.replaceChildren(
    el("span", {}, [
      `${glyph} `,
      el("span", { class: "toolbar-pill-word", text: word }),
    ]),
  );
}

/** The platform-correct modifier glyph for tooltips (⌘ on macOS, Ctrl elsewhere). */
const IS_MAC = window.chiphippo?.platform === "darwin";
const MOD_KEY = IS_MAC ? "⌘" : "Ctrl";

/** A menu-style accelerator hint ("⇧⌘S" on macOS, "Shift+Ctrl+S" elsewhere) —
    the same accelerators the native File menu registers (main.js). */
const accel = (key, shift = false) =>
  IS_MAC ? `${shift ? "⇧" : ""}⌘${key}` : `${shift ? "Shift+" : ""}Ctrl+${key}`;

/** The system (settings) gear icon for the top-right header action. */
const GEAR_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" ' +
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="3"/>' +
  '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06' +
  "-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A" +
  "1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l" +
  ".06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1" +
  ".65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l" +
  ".06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.6" +
  "5 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-." +
  "06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-." +
  '09a1.65 1.65 0 0 0-1.51 1z"/></svg>';

/** Schematic file icons — the File pill's Save button and the file menu's
    New / Open / Save items. */
const ICON_SVG_OPEN =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" ' +
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
const NEW_SVG =
  ICON_SVG_OPEN +
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
  '<polyline points="14 2 14 8 20 8"/>' +
  '<line x1="12" y1="18" x2="12" y2="12"/>' +
  '<line x1="9" y1="15" x2="15" y2="15"/></svg>';
const LOAD_SVG =
  ICON_SVG_OPEN +
  '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 ' +
  '0 0 1 2 2z"/></svg>';
const SAVE_SVG =
  ICON_SVG_OPEN +
  '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>' +
  '<polyline points="17 21 17 13 7 13 7 21"/>' +
  '<polyline points="7 3 7 8 15 8"/></svg>';

/** Save As — the floppy left open at the corner a pencil writes into. */
const SAVE_AS_SVG =
  ICON_SVG_OPEN +
  '<path d="M13 19H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9l4 4v3"/>' +
  '<polyline points="7 3 7 8 13 8"/>' +
  '<path d="M19 13a1.8 1.8 0 0 1 2.5 2.5L16.5 20.5l-3.5 1 1-3.5z"/></svg>';

/** Connectivity-probe icon for the Probe toolbar toggle: a probe tip landing
 * on a digital rising edge, with a cable trailing off the handle end. */
const PROBE_SVG =
  ICON_SVG_OPEN +
  '<path d="M3 19h4v-6h5"/>' +
  '<line x1="20" y1="4" x2="13" y2="11"/>' +
  '<path d="M20 4c1-1.5 3-1.5 3 0"/></svg>';

/** Cut Track icon (veroboard): a copper strip broken where a spot-face cutter
 * has come down on it — the cutter above, the gap and the bare hole below. */
const CUT_SVG =
  ICON_SVG_OPEN +
  '<path d="M2 18h6M16 18h6"/>' +
  '<circle cx="12" cy="18" r="2"/>' +
  '<path d="M12 3v7"/>' +
  '<path d="M9.5 10h5L12 14z"/></svg>';

/** Logic-analyzer icon for the Analyzer toolbar toggle: a recorded digital
 * waveform — what the panel draws. */
const ANALYZER_SVG =
  ICON_SVG_OPEN +
  '<polyline points="2 18 6 18 6 6 11 6 11 18 16 18 16 6 22 6"/></svg>';

/** Multimeter icon for the toolbar segment that takes another meter out
 * (up to three): the body, its display and its dial. */
const METER_SVG =
  ICON_SVG_OPEN +
  '<rect x="5" y="2" width="14" height="20" rx="2"/>' +
  '<rect x="8" y="5" width="8" height="4" rx="0.5"/>' +
  '<circle cx="12" cy="15" r="3"/>' +
  '<path d="M12 15l2-2"/></svg>';

/**
 * Auto-route icon — ONE run threaded from end to end: a node, three straight
 * board-aligned legs folded back on each other by two half-turns, and the node
 * it lands on. It draws what the button does — take a wire's two fixed ends and
 * find the way between them — rather than what the result looks like, which is
 * why the ENDS are drawn at all: the router never moves them, and everything in
 * between is what it decides.
 *
 * It has to be told apart from its neighbour at a glance, which is the one
 * constraint that has governed every version of this icon. FADE_WIRES_SVG is
 * also two circles joined by a line, so the difference has to be the LINE:
 * fade's is a single flat sagging jumper faded through the middle, this one a
 * stepped diagonal of square legs and half-turns filling the box corner to
 * corner. (The version before this drew two parallel runs and no ends at all —
 * legible, but it said "lanes" where the action is "find a path".)
 *
 * The two arcs are true semicircles of radius 3 (centres (15,9) and (9,15)), so
 * every fold is the same size and the legs stay a constant 6 units apart; the
 * bends are the widest points, at x 18 and 6, which is what keeps the glyph
 * optically centred against the circles' 19.5 and 4.5.
 */
const ROUTE_SVG =
  ICON_SVG_OPEN +
  '<circle cx="7" cy="6" r="2.5"/>' +
  '<circle cx="17" cy="18" r="2.5"/>' +
  '<path d="M9.5 6H15a3 3 0 0 1 0 6H9a3 3 0 0 0 0 6h5.5"/></svg>';

/** "Fade wires" toggle icon — the effect itself: two tie points joined by a
 * jumper drawn solid off each end and faded away in between. */
const FADE_WIRES_SVG =
  ICON_SVG_OPEN +
  '<path d="M4 7 Q12 19 20 7" opacity=".3"/>' +
  '<path d="M4 7 Q6.24 10.36 8.48 11.84"/>' +
  '<path d="M15.52 11.84 Q17.76 10.36 20 7"/>' +
  '<circle cx="4" cy="7" r="1.8"/>' +
  '<circle cx="20" cy="7" r="1.8"/></svg>';

/** Fit-to-screen ("locate") icon for the toolbar action that frames every
 * board/part/wire on the desk — a crosshair/target glyph. */
const LOCATE_SVG =
  ICON_SVG_OPEN +
  '<circle cx="12" cy="12" r="10"/>' +
  '<line x1="22" y1="12" x2="18" y2="12"/>' +
  '<line x1="6" y1="12" x2="2" y2="12"/>' +
  '<line x1="12" y1="6" x2="12" y2="2"/>' +
  '<line x1="12" y1="22" x2="12" y2="18"/></svg>';

/** Zoom-out-fully glyph the Fit-to-screen button swaps to while hovered with
 * Shift held (⌘⇧F previews as a magnifying glass with a "−"). */
const ZOOM_OUT_SVG =
  ICON_SVG_OPEN +
  '<circle cx="11" cy="11" r="8"/>' +
  '<line x1="21" y1="21" x2="16.65" y2="16.65"/>' +
  '<line x1="8" y1="11" x2="14" y2="11"/></svg>';

/** The AI segment's tooltip while it is available (see `refreshAiReady`). A
    function, not a const: `t()` must never be called at module scope, where the
    catalog is not loaded yet (see i18n.js). */
const aiTitle = () => t("toolbar.ai.title");

/** AI-builder icon: a four-point spark — the app-wide "generated" glyph. */
const AI_SVG =
  ICON_SVG_OPEN +
  '<path d="M12 3 13.8 9.2 20 11 13.8 12.8 12 19 10.2 12.8 4 11 10.2 9.2Z"/>' +
  '<path d="M18.5 3.5 19.2 5.8 21.5 6.5 19.2 7.2 18.5 9.5 17.8 7.2 15.5 6.5 ' +
  '17.8 5.8Z"/></svg>';

/** Schematic-view icon: what the view itself draws — two symbol boxes joined
 * by one orthogonally routed net. */
const SCHEMATIC_SVG =
  ICON_SVG_OPEN +
  '<rect x="2.5" y="3.5" width="7.5" height="6.5" rx="1"/>' +
  '<rect x="14" y="14" width="7.5" height="6.5" rx="1"/>' +
  '<path d="M10 6.75h2.25v10.5H14"/></svg>';

/** Breadboard icon the Schematic toggle swaps to while the diagram is
 * showing: a board's two rows of tie points either side of the trench. */
const BREADBOARD_SVG =
  ICON_SVG_OPEN +
  '<rect x="2.5" y="4.5" width="19" height="15" rx="2"/>' +
  '<line x1="2.5" y1="12" x2="21.5" y2="12"/>' +
  '<line x1="7" y1="8" x2="7.01" y2="8"/>' +
  '<line x1="12" y1="8" x2="12.01" y2="8"/>' +
  '<line x1="17" y1="8" x2="17.01" y2="8"/>' +
  '<line x1="7" y1="16" x2="7.01" y2="16"/>' +
  '<line x1="12" y1="16" x2="12.01" y2="16"/>' +
  '<line x1="17" y1="16" x2="17.01" y2="16"/></svg>';

/** 3D-view icon: a cube seen from above a corner — the view it swaps to. */
const VIEW3D_SVG =
  ICON_SVG_OPEN +
  '<path d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7Z"/>' +
  '<path d="M3.5 7 12 11.5 20.5 7"/>' +
  '<line x1="12" y1="11.5" x2="12" y2="21.5"/></svg>';

/** Build-guide (clipboard-list) icon for the Guide toolbar toggle. */
const GUIDE_SVG =
  ICON_SVG_OPEN +
  '<rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>' +
  '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 ' +
  '1 2-2h2"/>' +
  '<line x1="12" y1="11" x2="16" y2="11"/>' +
  '<line x1="12" y1="16" x2="16" y2="16"/>' +
  '<line x1="8" y1="11" x2="8.01" y2="11"/>' +
  '<line x1="8" y1="16" x2="8.01" y2="16"/></svg>';

/**
 * The header bar. Returns the elements whose LABELS have to be re-applied when
 * the language changes (`relabelChrome` in init()) rather than just the finished
 * node — Chip Hippo never reloads the window, so a locale change has to be
 * applied to the chrome that is already on screen.
 * @returns {{header: HTMLElement, iconBtn: HTMLElement, settingsBtn: HTMLElement}}
 */
function buildHeader() {
  const header = document.createElement("header");
  header.className = "app-header";
  header.setAttribute("aria-label", t("app.header"));

  const brand = document.createElement("div");
  brand.className = "app-header-brand";

  const icon = document.createElement("img");
  icon.className = "app-header-icon";
  icon.src = "chiphippo-icon.svg";
  icon.width = 28;
  icon.height = 28;
  icon.alt = "";
  icon.setAttribute("aria-hidden", "true");
  icon.draggable = false;

  // The icon is a button that opens the About dialog (the app-name affordance).
  const iconBtn = document.createElement("button");
  iconBtn.className = "app-header-icon-btn";
  iconBtn.type = "button";
  iconBtn.title = t("app.about");
  iconBtn.setAttribute("aria-label", t("app.about"));
  iconBtn.append(icon);
  iconBtn.addEventListener("click", () => AboutDialog.open());

  const logo = document.createElement("span");
  logo.className = "app-logo";
  logo.textContent = "Chip Hippo";

  brand.append(iconBtn, logo);

  // Empty toolbar slot — later stages mount desk tools (add board, …).
  const toolbar = document.createElement("div");
  toolbar.className = "app-header-toolbar";
  toolbar.id = "app-toolbar";

  // Right-aligned action panel: the system (settings) icon. Opening Settings
  // goes through the same chiphippo:open-settings event the menu uses, so the
  // dialog is seeded with the current settings in one place (app.js).
  const actions = document.createElement("div");
  actions.className = "header-icon-panel";
  actions.setAttribute("role", "toolbar");
  actions.setAttribute("aria-label", t("app.actions"));

  const settingsBtn = document.createElement("button");
  settingsBtn.className = "icon-btn header-icon-btn";
  settingsBtn.type = "button";
  settingsBtn.title = t("app.settings");
  settingsBtn.setAttribute("aria-label", t("app.openSettings"));
  settingsBtn.innerHTML = GEAR_SVG;
  settingsBtn.addEventListener("click", () =>
    window.dispatchEvent(new CustomEvent("chiphippo:open-settings")),
  );
  // The web build has no application menu bar (.claude/rules/web.md), so its
  // Help menu — main's own, guide, support, shortcuts, updates, About — hangs
  // from a book beside the gear. The host draws the menu; this only asks.
  if (isWeb()) {
    const helpBtn = document.createElement("button");
    helpBtn.className = "icon-btn header-icon-btn";
    helpBtn.type = "button";
    helpBtn.title = t("menu.help.title");
    helpBtn.setAttribute("aria-label", t("menu.help.title"));
    helpBtn.setAttribute("aria-haspopup", "menu");
    helpBtn.setAttribute("aria-expanded", "false");
    helpBtn.innerHTML = bookSvg(16);
    helpBtn.addEventListener("click", () =>
      window.dispatchEvent(
        new CustomEvent("chiphippo:web-help-menu", {
          detail: { anchor: helpBtn },
        }),
      ),
    );
    actions.append(helpBtn);
  }
  actions.append(settingsBtn);

  header.append(brand, toolbar, actions);
  return { header, iconBtn, settingsBtn };
}

function buildDesk() {
  const desk = document.createElement("section");
  desk.className = "desk-viewport";
  desk.setAttribute("aria-label", t("app.desk"));

  // Inert overlay hint (pointer-events: none) — Feature 30's "add board"
  // flow replaces it.
  const hint = document.createElement("p");
  hint.className = "desk-hint";
  hint.textContent = t("app.deskHint");

  desk.append(hint);
  return desk;
}

/** One File-pill segment: icon-only, its name and accelerator in the tooltip. */
function fileSegment({
  icon,
  label,
  title,
  haspopup = false,
  onClick,
  onContextMenu = null,
}) {
  // prettier-ignore
  const btn = el("button", {
    class: "toolbar-pill-btn toolbar-pill-btn--icon",
    type: "button",
    title,
    "aria-label": label,
    "aria-haspopup": haspopup ? "menu" : null,
    onClick,
    onContextMenu,
  });
  btn.innerHTML = icon;
  return btn;
}

/**
 * File actions — a PILL, the same shape the desk tools use: one border around a
 * row of borderless segments. They act on the PROJECT, which is the document:
 * one file holds every desktop, so New / Open / Save / Save As mean one thing
 * each. Every file action is its own segment rather than a row hidden behind a
 * ▾ — they're peers, and a toolbar's job is to show what's available.
 *
 * Each dispatches the SAME `chiphippo:*` event the native File menu pushes, so
 * the two can't drift. An MRU list still can't be a BUTTON, but it is what
 * Open's SECONDARY click offers — the same split the tab strip's "+" uses: a
 * primary click does the common thing, a secondary one drops the menu behind
 * it. That one goes straight to the workspace rather than through a push, since
 * the native menu bakes its recent list into the menu template and there is no
 * item here to drift from — only the same list, asked for as the card opens.
 *
 * DESKTOP actions have no toolbar button at all: Add / Duplicate / Import /
 * Export / Properties / Delete are the application's **Desktop** menu (main.js
 * `buildAppMenu`) and the tab strip's own context menu — a desktop is reached
 * through its tab, so that is where the things one can do to it belong.
 *
 * @param {() => object|null} getWorkspace - late-bound: the pill is built
 *   before the workspace, and only a CLICK ever needs it.
 */
function buildFilePill(getWorkspace) {
  /** Every segment fires the File menu's own push — one code path, two UIs. */
  const fileAction = (event) => () =>
    window.dispatchEvent(new CustomEvent(`chiphippo:${event}`));

  const buttons = {
    new: fileSegment({
      icon: NEW_SVG,
      label: t("toolbar.file.new"),
      title: t("toolbar.file.newTitle", { accel: accel("N") }),
      onClick: fileAction("project-new"),
    }),
    open: fileSegment({
      icon: LOAD_SVG,
      label: t("toolbar.file.open"),
      // Two accelerators in one tooltip: the segment's own, and the chord that
      // drops the menu its secondary click does (bindShortcuts).
      title: t("toolbar.file.openTitle", {
        accel: accel("O"),
        recent: accel("O", true),
      }),
      onClick: fileAction("project-open"),
      onContextMenu: (e) => {
        e.preventDefault();
        void getWorkspace()?.openRecentMenu(e.clientX, e.clientY);
      },
    }),
    save: fileSegment({
      icon: SAVE_SVG,
      label: t("toolbar.file.save"),
      title: t("toolbar.file.saveTitle", { accel: accel("S") }),
      onClick: fileAction("project-save"),
    }),
    saveAs: fileSegment({
      icon: SAVE_AS_SVG,
      label: t("toolbar.file.saveAs"),
      title: t("toolbar.file.saveAsTitle", { accel: accel("S", true) }),
      onClick: fileAction("project-save-as"),
    }),
  };
  const pill = el(
    "div",
    {
      class: "toolbar-pill",
      role: "group",
      "aria-label": t("toolbar.file.group"),
    },
    [buttons.new, buttons.open, buttons.save, buttons.saveAs],
  );
  return { pill, buttons };
}

/**
 * The simulation transport (Features 90/100) — its own pill, sitting apart from
 * the edit tools. It is the ONE pill whose segment count changes: stopped it
 * holds exactly Run; the moment the circuit runs that segment becomes Stop and
 * Pause / Step / speed unhide beside it, so it never offers a control that does
 * not apply. `onTransportChange` in init() owns that flip — this only builds it.
 *
 * The glyph leading each label is NOT part of the translation: ▶ / ⏸ / ⇥ / ■
 * mean the same thing in every language, so a catalog carrying them would only
 * give six chances to lose one.
 *
 * @param {() => object|null} getSim - late-bound: the SimController is built
 *   from `onTransportChange`, which needs these buttons, so the pill comes
 *   first and only a CLICK ever needs the transport.
 */
function buildTransportPill(getSim) {
  const pill = el("div", {
    class: "toolbar-pill toolbar-pill--transport",
    role: "group",
    "aria-label": t("toolbar.transport.group"),
  });
  const buttons = {
    run: el("button", {
      class: "toolbar-pill-btn toolbar-pill-btn--run",
      type: "button",
      title: t("toolbar.transport.runTitle", { mod: MOD_KEY }),
      "aria-pressed": "false",
      onClick: () => getSim().toggle(),
    }),
    pause: el("button", {
      class: "toolbar-pill-btn",
      type: "button",
      title: t("toolbar.transport.pauseTitle"),
      hidden: true,
      onClick: () => getSim().togglePause(),
    }),
    step: el("button", {
      class: "toolbar-pill-btn",
      type: "button",
      title: t("toolbar.transport.stepTitle"),
      hidden: true,
      onClick: () => getSim().step(),
    }),
    speed: el("button", {
      class: "toolbar-pill-btn toolbar-pill-btn--speed",
      type: "button",
      text: "×1",
      title: t("toolbar.transport.speedTitle"),
      hidden: true,
      onClick: () => {
        const sim = getSim();
        const i = (SPEEDS.indexOf(sim.speed) + 1) % SPEEDS.length;
        sim.setSpeed(SPEEDS[i]);
        showSpeed(buttons.speed, SPEEDS[i], null);
      },
    }),
  };
  setTransportFace(buttons.run, "▶", t("toolbar.transport.run"));
  setTransportFace(buttons.pause, "⏸", t("toolbar.transport.pause"));
  setTransportFace(buttons.step, "⇥", t("toolbar.transport.step"));
  pill.append(buttons.run, buttons.pause, buttons.step, buttons.speed);
  return { pill, buttons };
}

/**
 * Fit to screen: recentre the desk on the origin, then frame every board, part
 * and wire on it (find lost parts). It stays available while the circuit runs —
 * the recentre is a document edit, so it alone is skipped.
 *
 * Shift previews the OTHER find-a-lost-part move (zoom out fully, ⌘⇧F). The
 * icon and tooltip swap while the button is hovered AND Shift is held: a pure
 * preview, no click involved, so the two conditions are tracked independently
 * and the label recomputed on either change. `blur` is in there because a Shift
 * released outside the window never fires our own keyup — it is the only
 * reliable place left to drop a stuck preview.
 *
 * @returns {{button: HTMLElement, relabel: () => void}} — `relabel` is also
 *   what a language change calls, since the label already depends on state.
 */
function buildFitSegment({ getActiveView, fitActiveView }) {
  let hovered = false;
  let shiftHeld = false;
  const button = el("button", {
    class: "toolbar-pill-btn toolbar-pill-btn--icon",
    type: "button",
    "aria-label": t("toolbar.fit.label"),
    title: t("toolbar.fit.title", { mod: MOD_KEY }),
    onClick: (e) => {
      if (e.shiftKey) getActiveView().zoomOutFull();
      else fitActiveView();
    },
  });
  const relabel = () => {
    const zoomOutFull = hovered && shiftHeld;
    button.innerHTML = zoomOutFull ? ZOOM_OUT_SVG : LOCATE_SVG;
    button.setAttribute(
      "aria-label",
      zoomOutFull ? t("toolbar.zoomOut.label") : t("toolbar.fit.label"),
    );
    button.title = zoomOutFull
      ? t("toolbar.zoomOut.title", { mod: MOD_KEY })
      : t("toolbar.fit.title", { mod: MOD_KEY });
  };
  button.innerHTML = LOCATE_SVG;
  button.addEventListener("pointerenter", () => {
    hovered = true;
    relabel();
  });
  button.addEventListener("pointerleave", () => {
    hovered = false;
    relabel();
  });
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Shift" || shiftHeld) return;
    shiftHeld = true;
    relabel();
  });
  window.addEventListener("keyup", (e) => {
    if (e.key !== "Shift") return;
    shiftHeld = false;
    relabel();
  });
  window.addEventListener("blur", () => {
    if (!shiftHeld) return;
    shiftHeld = false;
    relabel();
  });
  return { button, relabel };
}

/**
 * The FILE and DESKTOP menus, and the close guard — every one of them the
 * workspace's to answer, since it is the only side that knows what is open and
 * what is unsaved.
 *
 * The application menu (main.js `buildAppMenu`) pushes one event per item. FILE
 * acts on the project — the document — and the toolbar's File pill dispatches
 * the very same events, so the two can't drift. DESKTOP edits the structure
 * inside it, and acts on whichever desktop is on screen. Open Recent is the one
 * push that carries a payload: the project file its item stands for.
 *
 * @param {object} bridge - `window.chiphippo`, for the close reply.
 * @param {() => object|null} getWorkspace - late-bound: these are registered
 *   before the workspace is built, and only a menu CLICK ever needs it.
 * @param {(id: string, format: string) => Promise<boolean>} exportDesktopTo -
 *   Export To (Feature 390), late-bound the same way.
 */
function wireProjectMenu(bridge, getWorkspace, exportDesktopTo) {
  /** A Desktop-menu item aimed at whichever desktop is on screen. */
  const activeDesktopAction = (method) => {
    const workspace = getWorkspace();
    const id = workspace?.activeTab?.id;
    if (id) return workspace[method](id);
  };

  for (const [event, run] of [
    ["chiphippo:project-new", () => getWorkspace()?.newProject()],
    ["chiphippo:project-open", () => getWorkspace()?.loadProject()],
    ["chiphippo:project-save", () => getWorkspace()?.save()],
    ["chiphippo:project-save-as", () => getWorkspace()?.saveAs()],
    ["chiphippo:project-properties", () => getWorkspace()?.editProjectProperties()], // prettier-ignore
    ["chiphippo:desktop-add", () => getWorkspace()?.addTab()],
    ["chiphippo:desktop-import", () => getWorkspace()?.importTab()],
    ["chiphippo:desktop-duplicate", () => activeDesktopAction("duplicateTab")],
    ["chiphippo:desktop-export", () => activeDesktopAction("exportTab")],
    ["chiphippo:desktop-properties", () => activeDesktopAction("editTabProperties")], // prettier-ignore
    ["chiphippo:desktop-delete", () => activeDesktopAction("deleteTab")],
  ]) {
    window.addEventListener(event, () => void run());
  }
  window.addEventListener("chiphippo:project-open-recent", (e) => {
    if (e.detail) void getWorkspace()?.openRecentProject(e.detail);
  });
  // Desktop ▸ Export To ▸ a format (Feature 390): the second push that carries
  // a payload — which format — aimed at the desktop on screen.
  window.addEventListener("chiphippo:desktop-export-to", (e) => {
    const id = getWorkspace()?.activeTab?.id;
    if (id && e.detail) void exportDesktopTo(id, e.detail);
  });

  // A part's EXAMPLE CIRCUIT, asked for from its pin-assignments window. That
  // window has a ref and nothing else — no project, no desk — so main relays
  // the request here, where both live (the memory inspector's host relay is the
  // same pipe). The workspace does the rest: a NEW example desktop is centred,
  // framed and left clean, and one that was already open is simply put back on
  // the desk, camera and all.
  window.addEventListener("chiphippo:demo-host-inbound", (e) => {
    const ref = e.detail?.ref;
    if (ref) void getWorkspace()?.openExample(ref);
  });

  // ── Closing the window / quitting ────────────────────────────────────────
  // Main prevents the close and asks HERE, because the unsaved state and the
  // dialog that deals with it both live in the renderer. Desktops are written
  // deliberately, never autosaved, so without this a • on a tab dies with the
  // window. It must reply EXACTLY once, whatever happens: main waits for the
  // answer with no timeout (the user is entitled to think about it), and it
  // LATCHES until the reply comes — so a guard that never answered would leave
  // an app that cannot be closed at all. `confirmClose()` therefore guarantees
  // both that it settles and that it never rejects.
  //
  // The default here used to be `true` — let the close proceed if the guard
  // threw — on the reasoning that a broken guard must not wedge the app. But
  // main's reply latch is per-attempt, so blocking is only ever ONE refused
  // ⌘Q, while proceeding is the project gone: `true` was trading the user's
  // unsaved work for an inconvenience. There is nothing here that can tell a
  // failed question from an answered one, so it has to assume the worst.
  window.addEventListener("chiphippo:confirm-close", async () => {
    let ok = false;
    try {
      const workspace = getWorkspace();
      ok = workspace ? await workspace.confirmClose() : true;
    } catch (err) {
      console.error("[renderer] close guard failed:", err);
    }
    bridge.closeReply(ok).catch((err) => {
      console.error("[renderer] app:close-reply failed:", err);
    });
  });
  // A close the guard said "go" to never happened (a restart-to-update whose
  // installer refused): the window lives on, so autosave must too.
  window.addEventListener("chiphippo:close-aborted", () => {
    getWorkspace()?.closeAborted();
  });
  // Only now is there anybody to ask. Main lets a close through until it hears
  // this — a page still booting holds nothing unsaved, and one that crashed or
  // never loaded would otherwise leave a window that could not be closed.
  Promise.resolve(bridge.closeReady?.()).catch((err) => {
    console.error("[renderer] app:close-ready failed:", err);
  });
}

/**
 * Edit ▸ Undo / Redo (⌘Z / ⇧⌘Z) and Select All (⌘A), pushed from the native
 * menu. Main pushes rather than running the native ROLE because "everything"
 * depends on where the focus is: a TEXT FIELD (the palette search, a Properties
 * dialog, a name prompt) selects its own text or keeps its own undo stack — the
 * document is not touched while typing — and the DESK selects every board, part
 * and wire, the same set a marquee takes, so ⌘A ⌘C copies the whole desktop.
 *
 * With a dialog open and the focus NOT in a field there is nothing to do:
 * selecting the desk underneath it would act on what the user cannot see.
 */
function wireEditMenu(controller) {
  const inTextField = () => {
    const node = document.activeElement;
    return (
      node &&
      (node.tagName === "INPUT" ||
        node.tagName === "TEXTAREA" ||
        node.isContentEditable)
    );
  };
  // Not under a dialog either: the native menu's ⌘Z still fires beneath an
  // HTML modal, and a Properties card or a pending confirm then acts on a part
  // or a board the undo just took away.
  const undoable = () => !inTextField() && !PopupManager.isOpen();
  window.addEventListener("chiphippo:edit-undo", () => {
    if (undoable()) controller.undo();
  });
  window.addEventListener("chiphippo:edit-redo", () => {
    if (undoable()) controller.redo();
  });
  window.addEventListener("chiphippo:edit-select-all", () => {
    const target = document.activeElement;
    const tag = target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") {
      target.select();
      return;
    }
    if (target?.isContentEditable) {
      document.execCommand("selectAll");
      return;
    }
    if (PopupManager.isOpen()) return;
    controller.selectAll();
  });
}

/**
 * Whether the AI builder has a connection to ask, and the button state that
 * follows. Decided WITHOUT reaching the provider (`ai/connection.js`): nothing
 * is sent anywhere until the user asks for a build, so this is a key stored for
 * the chosen provider, a provider this build has an adapter for, and a base URL
 * that parses. Whether the server would ACCEPT the key is Settings ▸ AI's Test
 * connection, and nothing else.
 *
 * The panel's RESTORE waits on that answer rather than firing at boot: an
 * `aiOpen` remembered from a session that HAD a key must not reopen a panel
 * whose button is now dead — and must not be overwritten either, since the key
 * may well come back. So the remembered state is honoured on the first
 * successful pass only, and never forced open under the user afterwards.
 *
 * @returns {() => Promise<void>} re-run it; every refusal carries the sentence
 *   the disabled button shows as its tooltip.
 */
function createAiReadiness({ bridge, aiPanel, getButton, config, remembered }) {
  let providers = null; // main's provider list, read once
  let restored = false; // whether the remembered `aiOpen` has been applied

  return async function refreshAiReady() {
    let ready = { ok: false, reason: t("toolbar.ai.unreadable") };
    try {
      providers ??= (await bridge.ai?.providers?.()) ?? [];
      const cfg = config();
      // The key is asked about for the provider `checkConnection` will judge —
      // one rule, so the button can never be gated on a key for a provider the
      // Settings panel is not showing.
      const provider = effectiveProvider(cfg, providers)?.id;
      const status = provider ? await bridge.ai?.key?.status(provider) : null;
      ready = checkConnection(cfg, providers, status);
    } catch (err) {
      console.error("[renderer] ai readiness check failed:", err);
    }
    const button = getButton();
    if (button) {
      button.disabled = !ready.ok;
      button.title = ready.ok ? aiTitle() : ready.reason;
    }
    if (ready.ok) {
      if (restored === false) aiPanel.setVisible(remembered);
    } else if (aiPanel.visible) {
      // A key cleared while the panel is open leaves a panel no button can
      // close — so it goes with the connection it belonged to.
      aiPanel.setVisible(false);
    }
    restored = true;
  };
}

/**
 * Changing the language, IN PLACE.
 *
 * Chip Hippo NEVER reloads the window — an unsaved project lives only in
 * memory, so a reload would throw the user's work away to change a label. So a
 * language change is applied to the chrome that is already on screen.
 *
 * Only PERSISTENT chrome needs this. Everything transient — every dialog,
 * context menu, popover, notification and the palette's own part rows — is
 * built when it opens and therefore speaks the current language for free; the
 * native menu bar is main's, and it rebuilds itself off the same setting. What
 * is left is this toolbar, the docked panels, and the window title.
 *
 * `panels` each re-render their OWN chrome rather than app.js reaching into
 * them; a panel given as a thunk is one built later than this call.
 * `restate` are functions the app already had, whose labels depend on state —
 * re-running them unchanged is exactly a relabel.
 */
function createRelabeller({
  header,
  desk,
  hint,
  schematicViewport,
  view3dViewport,
  filePill,
  fileButtons,
  toolPill,
  transportPill,
  buttons,
  panels,
  restate,
}) {
  return () => {
    header.header.setAttribute("aria-label", t("app.header"));
    header.iconBtn.title = t("app.about");
    header.iconBtn.setAttribute("aria-label", t("app.about"));
    header.settingsBtn.title = t("app.settings");
    header.settingsBtn.setAttribute("aria-label", t("app.openSettings"));

    desk.setAttribute("aria-label", t("app.desk"));
    hint.textContent = t("app.deskHint");
    schematicViewport.setAttribute("aria-label", t("app.schematic"));
    view3dViewport.setAttribute("aria-label", t("app.view3d"));

    filePill.setAttribute("aria-label", t("toolbar.file.group"));
    for (const [btn, label, title, params] of [
      [fileButtons.new, "new", "newTitle", { accel: accel("N") }],
      [fileButtons.open, "open", "openTitle", { accel: accel("O"), recent: accel("O", true) }], // prettier-ignore
      [fileButtons.save, "save", "saveTitle", { accel: accel("S") }],
      [fileButtons.saveAs, "saveAs", "saveAsTitle", { accel: accel("S", true) }], // prettier-ignore
    ]) {
      btn.setAttribute("aria-label", t(`toolbar.file.${label}`));
      btn.title = t(`toolbar.file.${title}`, params);
    }

    toolPill.setAttribute("aria-label", t("toolbar.tools.group"));
    buttons.wireBtn.title = t("toolbar.wire.title");
    buttons.wireBtn.querySelector("span:not(.wire-color-dot)").textContent =
      t("toolbar.wire.label");
    buttons.busBtn.title = t("toolbar.bus.title");
    buttons.busBtn.querySelector("span:not(.bus-width-badge)").textContent =
      t("toolbar.bus.label");
    for (const [btn, key, params] of [
      [buttons.cutBtn, "cut", null],
      [buttons.routeBtn, "route", null],
      [buttons.fadeBtn, "fade", null],
      [buttons.probeBtn, "probe", null],
      [buttons.scopeBtn, "analyzer", { mod: MOD_KEY }],
      [buttons.meterBtn, "meter", null],
      [buttons.guideBtn, "bom", { mod: MOD_KEY }],
    ]) {
      btn.setAttribute("aria-label", t(`toolbar.${key}.label`));
      btn.title = t(`toolbar.${key}.title`, params ?? undefined);
    }
    buttons.aiBtn.setAttribute("aria-label", t("toolbar.ai.label"));

    transportPill.setAttribute("aria-label", t("toolbar.transport.group"));
    setTransportFace(buttons.stepBtn, "⇥", t("toolbar.transport.step"));
    buttons.stepBtn.title = t("toolbar.transport.stepTitle");
    buttons.speedBtn.title = t("toolbar.transport.speedTitle");
    buttons.pauseBtn.title = t("toolbar.transport.pauseTitle");

    for (const panel of panels) {
      (typeof panel === "function" ? panel() : panel)?.relocalize();
    }
    for (const restated of restate) restated();
  };
}

/**
 * The desk-tool pill: Wire · Bus · Fade · Probe · Analyzer · Fit · BOM ·
 * Schematic · AI.
 *
 * Nine tools that read as ONE control — a single rounded surface carrying the
 * only border, its segments separated by spacing rather than by borders of
 * their own. Each is still an ordinary button with its own state; only the
 * chrome is shared. Append order IS the layout.
 *
 * Two segments carry a READOUT that is also its own PICKER — the Wire dot and
 * the Bus badge. Each is a <span> inside its one <button> (a nested <button> is
 * invalid HTML, and the pill has no split seam), and picking deliberately does
 * NOT arm the tool: the segment already arms when its label is clicked, so the
 * readout must be the one place that doesn't, or there would be no way to set
 * the pending option without entering the tool.
 *
 * Which segments stay live while the circuit RUNS is decided in init(): the
 * passive viewers (Probe, Analyzer, BOM, Schematic, AI) do; Wire and Bus edit
 * topology and are disabled.
 *
 * @returns {{pill, buttons, relabelFit}} — `relabelFit` is the Fit segment's
 *   own state-dependent relabel, which a language change also calls.
 */
function buildDeskToolPill({
  controller,
  scopeView,
  buildGuide,
  aiPanel,
  setMode,
  getMode,
  getActiveView,
  fitActiveView,
  onAutoRoute,
}) {
  const pill = el("div", {
    class: "toolbar-pill toolbar-pill--tools",
    role: "group",
    "aria-label": t("toolbar.tools.group"),
  });

  /** The five icon-only toggles, which differ only in icon, keys and action. */
  const iconSegment = (icon, label, title, onClick, pressed = false) => {
    const btn = el("button", {
      class: "toolbar-pill-btn toolbar-pill-btn--icon",
      type: "button",
      "aria-label": label,
      title,
      "aria-pressed": String(pressed),
      onClick,
    });
    btn.innerHTML = icon;
    btn.classList.toggle("toolbar-btn--active", pressed);
    return btn;
  };

  // Wire tool (shortcut W), with the colour dot that is also the eight-swatch
  // picker. While the circuit runs the button is disabled, which makes the dot
  // inert for free (a disabled <button> still delivers a click to a descendant,
  // so the dot asks the button it is in — see wire-color-dot.js).
  const wireDot = createWireColorDot({
    getColor: () => controller.wireColor,
    onPick: (color) => controller.setWireColor(color),
  });
  const wire = el(
    "button",
    {
      class: "toolbar-pill-btn toolbar-pill-btn--tool",
      type: "button",
      title: t("toolbar.wire.title"),
      "aria-pressed": "false",
      onClick: () => controller.toggleWireTool(),
    },
    [el("span", { text: t("toolbar.wire.label") }), wireDot.element],
  );

  // Bus tool (shortcut B) — a multi-bit run of wires in one gesture, riding the
  // active wire colour. Its badge shows the active width (2–8 or 16 bits) and,
  // like the dot beside it, is also the picker for it.
  const busWidth = createBusWidthBadge({
    getName: () => controller.busName,
    onPick: (name) => controller.setBusName(name),
  });
  const bus = el(
    "button",
    {
      class: "toolbar-pill-btn toolbar-pill-btn--tool",
      type: "button",
      title: t("toolbar.bus.title"),
      "aria-pressed": "false",
      onClick: () => controller.toggleBusTool(),
    },
    [el("span", { text: t("toolbar.bus.label") }), busWidth.element],
  );

  // Cut Track (shortcut K): a veroboard's copper cut, or a cut put back, a
  // click at a hole. An EDIT, so it goes in `editButtons` with Wire and Bus.
  const cut = iconSegment(
    CUT_SVG,
    t("toolbar.cut.label"),
    t("toolbar.cut.title"),
    () => controller.toggleCutTool(),
  );

  // Auto-route (Feature 360): rewrite every wire as a board-aligned run around
  // the parts. A one-shot ACTION, not a mode — like Fit, it arms nothing and
  // opens nothing — but unlike Fit it EDITS, so it goes in `editButtons` and
  // goes dead while the circuit runs.
  // Option-click additionally leaves the router's own view of the desk on
  // screen — the region, the bridges, the lanes, and every obstacle as
  // INFLATED. A second Option-click clears it.
  const route = iconSegment(
    ROUTE_SVG,
    t("toolbar.route.label"),
    t("toolbar.route.title"),
    (e) => onAutoRoute(e?.altKey === true),
  );
  // Fade wires: every wire as a short stub off each hole, fading out between,
  // so a heavily wired board stays readable. A selected wire comes back whole.
  // Purely how the desk is DRAWN, so it stays live while running.
  const fade = iconSegment(
    FADE_WIRES_SVG,
    t("toolbar.fade.label"),
    t("toolbar.fade.title"),
    () => controller.toggleWiresFaded(),
  );
  // Probe: highlight a whole electrical net on hover (shortcut I).
  const probe = iconSegment(
    PROBE_SVG,
    t("toolbar.probe.label"),
    t("toolbar.probe.title"),
    () => controller.toggleProbe(),
  );
  // Logic analyzer. No `mod` to interpolate: it is a BARE `A`, like the desk's
  // other tool letters — ⌘A is Select All, which the native Edit menu owns.
  const scope = iconSegment(
    ANALYZER_SVG,
    t("toolbar.analyzer.label"),
    t("toolbar.analyzer.title"),
    () => scopeView.toggle(),
    scopeView.visible,
  );
  // The multimeter (catalog/meter.js): takes another one out, up to
  // MAX_METERS — the bench's instrument, so it stays live while running. Each
  // is put away from its own menu. Offered while Spice Lite is on, and
  // disabled at the limit (`syncMeter`, on every edit and desk loaded).
  const meter = iconSegment(
    METER_SVG,
    t("toolbar.meter.label"),
    t("toolbar.meter.title"),
    () => controller.addMeter(),
  );
  // Fit to screen, with its Shift preview of zoom-out-fully.
  const { button: fit, relabel: relabelFit } = buildFitSegment({
    getActiveView,
    fitActiveView,
  });
  // Bill of materials (⌘B): the right-docked build guide — BOM, wiring list,
  // assembly steps. It reads the desk rather than editing it, so like the
  // analyzer it stays available while the circuit runs. Its armed state comes
  // from buildGuide's own onVisibilityChange, so the segment tracks the panel
  // however it was closed.
  const guide = iconSegment(
    GUIDE_SVG,
    t("toolbar.bom.label"),
    t("toolbar.bom.title", { mod: MOD_KEY }),
    () => buildGuide.toggle(),
    buildGuide.visible,
  );
  // Breadboard ⇄ Schematic (Tab). The one segment that swaps the VIEWPORT
  // rather than arming a tool or opening a panel, so its icon shows where it
  // would take you; `setMode` owns both states and Tab drives the same
  // function, so the key and the button can never disagree.
  const modeBtn = iconSegment(
    SCHEMATIC_SVG,
    t("toolbar.mode.schematic"),
    t("toolbar.mode.schematicTitle"),
    () => setMode(getMode() === "schematic" ? "desk" : "schematic"),
  );
  // The 3D view. Like Schematic it swaps the viewport rather than arming a
  // tool — a way of LOOKING at the desk, toggled on and off while looking at
  // it, so the view itself is a toolbar control and not a setting. Whether the
  // segment is OFFERED is one, though (Settings ▸ Appearance ▸ 3D enabled, Off
  // by default — `applySettings` shows or hides it). The app always opens on
  // the breadboard. `setMode` owns its armed state and label.
  const view3d = iconSegment(
    VIEW3D_SVG,
    t("toolbar.view3d.show"),
    t("toolbar.view3d.showTitle"),
    () => setMode(getMode() === "3d" ? "desk" : "3d"),
  );
  // AI builder. Its armed state comes from the panel's own onVisibilityChange,
  // and it stays available while the circuit runs — the panel itself refuses to
  // build while the desk is frozen, and saying so there is clearer than a dead
  // button here. It is DISABLED, though, until there is a connection to ask at
  // all: the builder is the one tool that cannot work on the user's own
  // machine, so offering it with no API key would be offering nothing. It
  // starts disabled and `createAiReadiness` enables it once main has answered,
  // replacing the tooltip with the reason whenever it is off.
  const ai = iconSegment(
    AI_SVG,
    t("toolbar.ai.label"),
    aiTitle(),
    () => aiPanel.toggle(),
    aiPanel.visible,
  );
  ai.disabled = true;

  pill.append(
    wire,
    bus,
    cut,
    route,
    fade,
    probe,
    scope,
    meter,
    fit,
    guide,
    modeBtn,
    view3d,
    ai,
  );
  return {
    pill,
    relabelFit,
    buttons: {
      wire,
      wireDot,
      bus,
      busWidth,
      cut,
      route,
      fade,
      probe,
      scope,
      meter,
      fit,
      guide,
      mode: modeBtn,
      view3d,
      ai,
    },
  };
}

/** The schematic surface (Feature 150) — a sibling of the desk, hidden until
    the Breadboard ⇄ Schematic toggle (or Tab) switches to it. */
function buildSchematicViewport() {
  const view = document.createElement("section");
  view.className = "schematic-viewport";
  view.setAttribute("aria-label", t("app.schematic"));
  view.hidden = true;
  return view;
}

/** The 3D view's surface — a sibling of the desk and the schematic, hidden
    until the 3D segment switches to it. */
function buildView3dViewport() {
  const view = document.createElement("section");
  view.className = "desk3d-viewport";
  view.setAttribute("aria-label", t("app.view3d"));
  view.hidden = true;
  return view;
}

/**
 * Central keyboard shortcuts: desk keys (Esc / Delete via DeskController)
 * first, then Space — held it pans, tapped it toggles Run/Stop (only when no
 * tool is armed) — then the
 * one FILE accelerator the native menu doesn't own (⇧⌘O, the recent projects),
 * then the app-chrome accelerators (analyzer / palette / run toggle / desk
 * lock).
 *
 * The app's two SCALES share one block: ⌘+, −, 0 resize Chip Hippo's own TEXT,
 * and ⌥⌘+, −, 0 zoom the desk CAMERA. Deliberately different things — one
 * resizes the chrome, the other zooms the circuit inside it.
 *
 * @param {(step: "in"|"out"|"reset") => void} onFontSize
 * @param {{setHeld: (on: boolean) => void, takePanned: () => boolean}} spacePan
 *   - the 2D views' Space-drag pan: told when Space goes down and up, and
 *   asked at the release whether the hold was used (a tap is Run/Stop).
 */
function bindShortcuts(
  controller,
  sim,
  scopeView,
  togglePalette,
  getActiveView,
  fitActiveView,
  onToggleView,
  onToggleLock,
  onOpenRecent,
  onFontSize,
  spacePan,
) {
  // Option/Alt held puts the desk in PIN-TARGETING mode: bodies go inert and
  // only a hole holding an independently movable lead (a wire end, a
  // two-terminal part's leg) lights, red, under the pointer (CLAUDE.md →
  // "Selection"). Its own listeners rather than a `handleKeyDown` case,
  // because that method's contract is "did I CONSUME this key" and a modifier
  // must not. Same trio as the Fit button's Shift-held zoom-out preview, blur
  // included — a modifier released outside the window never fires our keyup.
  const pinMode = (on) => controller.setPinMode(on);
  window.addEventListener("keydown", (e) => {
    if (e.key === "Alt") pinMode(true);
  });
  window.addEventListener("keyup", (e) => {
    if (e.key === "Alt") pinMode(false);
  });
  window.addEventListener("blur", () => pinMode(false));

  // SPACE is two things, told apart by what happens while it is down. HELD,
  // with a drag, it pans the desk from anywhere (a plain drag no longer does —
  // on the breadboard that is the rubber band). TAPPED, it runs/stops the
  // circuit — which therefore happens on the RELEASE, since only then is it
  // known that no pan used the hold. Its keyup and blur are their own
  // listeners for the signal keys' reason: a Space that went down on the desk
  // must come up wherever the focus has gone.
  let spaceDown = false;
  const typingIn = (e) => {
    const tag = e.target?.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
  };
  const releaseSpace = () => {
    if (!spaceDown) return false;
    spaceDown = false;
    spacePan.setHeld(false);
    return !spacePan.takePanned();
  };
  window.addEventListener("keyup", (e) => {
    if (e.key !== " " && e.code !== "Space") return;
    if (!releaseSpace()) return; // a hold that panned is not a tap
    if (PopupManager.isOpen() || typingIn(e)) return;
    // Not with a part in hand, and not with a wire or bus half laid (Run
    // would throw it away). An armed wire or bus tool with nothing laid is no
    // reason to refuse: neither tool uses Space, and Run disarms them both.
    if (controller.placementArmed || controller.layingInProgress) return;
    e.preventDefault();
    sim.toggle();
  });
  window.addEventListener("blur", () => {
    releaseSpace();
  });

  // External signals (Feature 370): 1–9, 0 press the buttons on the right-edge
  // rail, several at once. See model/signal-keys.js for why a BARE digit is
  // free — the only other claim on 1–9 is the wire tool's colour and the bus
  // tool's width, and Run disarms both tools, so the two meanings are never
  // live at the same moment.
  const signalKeys = createSignalKeys({
    isRunning: () => sim.running,
    signalsOf: () => controller.signals,
    press: (id, on) => sim.pressSignal(id, on),
  });
  // Keyup and blur are their OWN listeners, deliberately OUTSIDE the popup and
  // typing guards below: a key that went down on the desk and comes up inside
  // a dialog still has to come up. A stuck signal is far worse than a missed
  // press, which is why release is gated on nothing at all.
  window.addEventListener("keyup", (e) => signalKeys.handleKeyUp(e));
  window.addEventListener("blur", () => signalKeys.releaseAll());

  window.addEventListener("keydown", (e) => {
    // A dialog/menu owns the keyboard while it's open — its own handlers
    // (native Escape-to-cancel, button activation) must be the only thing
    // that reacts, so no desk/app shortcut leaks through underneath it.
    if (PopupManager.isOpen()) return;
    // Tab flips Breadboard ⇄ Schematic (Feature 150) — not while typing.
    if (
      e.key === "Tab" &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !e.shiftKey
    ) {
      const tag = e.target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      onToggleView?.();
      return;
    }
    if (controller.handleKeyDown(e)) {
      e.preventDefault();
      return;
    }
    // 1–9, 0 press the signal buttons, but only while the circuit runs. It sits
    // AFTER controller.handleKeyDown deliberately: that method claims 1–9 for
    // the wire colour / bus width while either tool is armed, so letting it go
    // first makes the precedence a FACT of the code rather than a claim about
    // which tools Run happens to disarm. Typing-guarded like Space and A — a
    // digit typed into an analyzer channel name is not a stimulus.
    {
      const tag = e.target?.tagName;
      const typing =
        tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
      if (!typing && signalKeys.handleKeyDown(e)) {
        e.preventDefault();
        return;
      }
    }
    // Space goes DOWN here and is acted on as it comes up (the keyup above):
    // held it is the pan key, tapped it is Run/Stop. Not while typing, and
    // not as part of a chord.
    if (e.key === " " || e.code === "Space") {
      if (typingIn(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault(); // no page scroll, no focused button pressed
      if (!spaceDown) {
        spaceDown = true;
        spacePan.setHeld(true);
      }
      return;
    }
    // A — the logic analyzer, on a BARE key like the desk's other tool
    // letters (W wire, B bus, I probe). Cmd/Ctrl+A is not this: it is
    // Select All, which the native Edit menu owns.
    if (
      (e.key === "a" || e.key === "A") &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey
    ) {
      // prettier-ignore
      const tag = e.target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      scopeView.toggle();
      return;
    }
    // The app's TWO SCALES, one block, because they differ by one modifier:
    // ⌘= / ⌘− / ⌘0 resize Chip Hippo's own text, and ⌥⌘= / ⌥⌘− / ⌥⌘0 zoom the
    // desk camera. Bare ⌘ is the TEXT because that is what a reader who cannot
    // see the screen reaches for first; the desk already has a zoom cluster, a
    // Fit button and the scroll wheel.
    //
    // AHEAD of the Cmd gate below, which discards every Alt chord — so the
    // desk pair could not reach anything past that line. Matched on `e.code`
    // inside `scaleStepForEvent`, not `e.key`: macOS rewrites the key NAME
    // under Option (⌥= is "≠"), so these are the one place in the app that
    // cannot use the usual convention.
    //
    // NOT typing-guarded, unlike the chrome toggles below: a view scale is
    // aimed at the app rather than at whatever has focus — the same reasoning
    // that puts ⇧⌘O ahead of that guard — and no field binding claims either
    // chord. Both stay UNDER the popup guard above, though: a dialog owns the
    // keyboard while it is open, and the Settings dialog is precisely the one
    // that would be, where its own picker would then be showing a size the app
    // had stopped using.
    const scale = scaleStepForEvent(e);
    if (scale) {
      e.preventDefault();
      if (scale.target === "font") {
        onFontSize(scale.step);
      } else {
        const view = getActiveView();
        if (scale.step === "in") view.zoomIn();
        else if (scale.step === "out") view.zoomOut();
        else view.resetZoom();
      }
      return;
    }
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    // ⇧⌘O — the recent projects, the same menu the toolbar's Open segment drops
    // on a secondary click. It sits AHEAD of the typing guard below, beside the
    // ⌘O it shadows: a file action is aimed at the app, not at whatever has
    // focus, and no browser binding claims this chord.
    if (e.shiftKey && (e.key === "o" || e.key === "O")) {
      e.preventDefault();
      onOpenRecent?.();
      return;
    }
    // Cmd/Ctrl+P / +R / +F / +L toggle app chrome — not while typing, where the
    // browser's own bindings must win.
    const tag = e.target?.tagName;
    const typing =
      tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
    if (!typing) {
      if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        togglePalette();
        return;
      }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        sim.toggle();
        return;
      }
      // The desk padlock. It goes through the padlock's own toggle rather than
      // straight to setWheelLocked, so the key and the button drive the one
      // path and the icon can never disagree with the wheel.
      if (e.key === "l" || e.key === "L") {
        e.preventDefault();
        onToggleLock();
        return;
      }
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        if (e.shiftKey) {
          getActiveView().zoomOutFull();
        } else {
          fitActiveView(); // the desk, or the schematic when it is showing
        }
        return;
      }
    }
  });

  // The transport's third release path for a held signal key (see
  // onTransportChange): keyup and window blur are the other two.
  return () => signalKeys.releaseAll();
}

async function init() {
  const bridge = window.chiphippo;

  // Resolve the active language and load its catalog BEFORE anything renders,
  // so the very first `t()` call — in buildHeader, below — resolves against the
  // right catalog rather than painting English and correcting itself. Main
  // decides which language that is (the persisted preference, else the OS
  // locale, else English) because it owns both the setting and the files; this
  // also sets <html lang>. See i18n.js.
  await i18n.init();

  // Load settings BEFORE mounting the desk so the saved viewport applies on
  // the first paint (acceptable to proceed with defaults if the read fails).
  let settings = {};
  try {
    settings = await bridge.settings.get();
  } catch (err) {
    console.error("[renderer] settings:get failed:", err);
  }

  // ── The application menu's own two items ──────────────────────────────────
  // The live settings document, and About / Settings / Keyboard Shortcuts,
  // registered HERE — before the project boots, before one panel is built.
  //
  // Everything below this point can fail. If it does, the window is still up
  // with a full menu bar on it, and items registered at the END of init()
  // would be dead in every one of them at once — an app that appears to
  // ignore the mouse rather than one that failed to load something. These
  // three depend on nothing but the settings just read, so they can always
  // answer, and the About card is where the version and the support address
  // are. `currentSettings` lives here rather than beside the Settings dialog
  // below because the AI panel also reads `ai` off it on every send — that is
  // how a Settings change reaches the panel with no wiring between them.
  let currentSettings = settings;

  // The country the OS is in: a new mains brick starts at its mains, and a
  // new AC adapter at its frequency (catalog/ac-parts.js `mainsForCountry`).
  let region = "";
  bridge
    .getRegion?.()
    .then((code) => (region = typeof code === "string" ? code : ""))
    .catch(() => {});
  // Settings, opened on a given panel — Run's "settings need to be verified"
  // and an element's "Manage connections…" go straight to Integration.
  // Spice Lite's family strip offers what the tray offers, which includes
  // any family the open project uses. The tray is built further down, after
  // the project boots — a getter that answers none until then, so Settings
  // opens whatever happened in between (a `palette?.` here read the `const`
  // before its declaration, and threw).
  let trayFamilies = () => [];
  const openSettings = (tab) =>
    SettingsDialog.open(currentSettings, {
      tab,
      projectFamilies: trayFamilies(),
    });
  window.addEventListener("chiphippo:show-about", () => AboutDialog.open());
  window.addEventListener("chiphippo:open-settings", () => openSettings());
  window.addEventListener("chiphippo:keyboard-shortcuts", () =>
    KeyboardShortcutsDialog.open(),
  );

  // Projects: there is ALWAYS one, and its active desktop is the document the
  // desk starts on. Read BEFORE anything mounts, so the app opens straight
  // onto that desktop instead of painting a blank desk and swapping it out a
  // moment later. Main decides which project that is (the unsaved one in its
  // saves folder, the most recently used one, or a brand-new one).
  let projectBoot = null;
  try {
    projectBoot = await ProjectWorkspace.boot(bridge);
  } catch (err) {
    console.error("[renderer] project boot failed:", err);
  }

  const app = document.getElementById("app");
  const desk = buildDesk();
  // Main row below the header: the parts palette (left, toggleable) beside
  // the full-bleed desk.
  const main = el("div", { class: "app-main" });
  const header = buildHeader();
  app.append(header.header, main);
  // What's new, for someone who used an earlier version (model/notices.js):
  // a bar between the toolbar and the desk until its ✕ is clicked, and a
  // closed notice stays closed — written here, as the capacitor note's
  // "Don't show again" is.
  const owed = pendingNotices(settings);
  const noticeBanner = owed.length
    ? new NoticeBanner(app, {
        notices: owed,
        before: main,
        onDismiss: (id) => {
          const dismissedNotices = [
            ...(currentSettings.dismissedNotices ?? []),
            id,
          ];
          currentSettings = { ...currentSettings, dismissedNotices };
          bridge.settings
            .set({ dismissedNotices })
            .catch((err) =>
              console.error("[renderer] settings:set failed:", err),
            );
        },
      })
    : null;
  let workspace = null;
  // Desktop ▸ Export To (Feature 390) — built once the toast stack exists;
  // the menu and the tab strip reach it late-bound, like the workspace.
  let exporter = null;
  const exportDesktopTo = (id, format) =>
    exporter?.export(workspace?.desktopSnapshot(id), format);

  // Desk document (Feature 20): the boards/components/wires of the desktop on
  // screen, held in one in-memory DeskDoc. Anything that mutates it dispatches
  // a global `chiphippo:doc-changed` CustomEvent. The desk is one DESKTOP of
  // the open PROJECT, which is the document: it is written to its one file
  // deliberately (⌘S), never autosaved, so the • in the title is what says the
  // work is only on screen.
  const deskDoc = new DeskDoc(projectBoot?.doc ?? null);
  // ONE conducting netlist cache shared by every consumer that needs the live
  // circuit (the sim, the probe's level tint, the analyzer, the review): a
  // topology change rebuilds the partition once instead of once per consumer,
  // and they can never tint from divergent nets.
  const netlistCache = new NetlistCache(deskDoc);
  // …and the WIRING-ONLY partition (every switch and button an open contact):
  // what the build connected, which is what the schematic draws, the build
  // guide lists, and the probe shows and NAMES — a closed switch must not
  // carry a net (or its name) from one side onto the other.
  const wiringNetlistCache = new NetlistCache(deskDoc, { bridges: false });

  // Debounced viewport persistence: every pan step emits a change, so writes
  // coalesce until the camera settles.
  let saveTimer = null;
  const scheduleViewportSave = (camera) => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      bridge.settings
        .set({ viewport: camera })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    }, VIEWPORT_SAVE_DEBOUNCE_MS);
  };

  // The window title names the project and the desktop on screen. Its • leads
  // the whole thing, and there is only one thing it can stand for now: the
  // project is the document, so its dirty flag covers every desktop's design,
  // the list of desktops, and their names alike.
  const updateTitle = () => {
    const tab = workspace?.activeTab;
    const marker = workspace?.dirty ? "• " : "";
    const project = workspace?.projectName || t("common.untitled");
    document.title = tab
      ? `${marker}${project} — ${tab.name} — Chip Hippo`
      : "Chip Hippo";
  };
  updateTitle();
  window.addEventListener("chiphippo:doc-changed", updateTitle);

  wireProjectMenu(bridge, () => workspace, exportDesktopTo);

  let zoomControl = null;
  let deskLock = null;
  let signalRail = null;
  // The Arduino serial integration's place in the window (rail cards, lamps,
  // the Generate segment, the run-time controller) — built once the
  // notification stack exists, below; the closures that reach it before then
  // only run on a user action.
  let integration = null;
  // Installed by bindShortcuts (below): let go of every held signal key.
  let releaseSignalKeys = null;
  let controller = null;
  // The chip designer (custom chips): the debugger's controller, built now
  // because the desk's menus read its arming, and the window's host bridge,
  // built once the workspace exists.
  const chipDebugger = new ChipDebugger({ deskDoc });
  let chipDesign = null;
  // The CPU monitor window's host side, built with the sim below.
  let cpuMonitor = null;
  // A designed chip's right-click in the tray: its design, a copy, or gone.
  const openCustomChipMenu = (id, x, y) => {
    const running = sim?.running === true;
    PopupManager.menu({
      x,
      y,
      items: [
        {
          label: t("palette.custom.menuEdit"),
          onSelect: () => chipDesign?.openDesign(id),
        },
        {
          label: t("palette.custom.menuDuplicate"),
          disabled: running,
          onSelect: () => chipDesign?.duplicateDesign(id),
        },
        { separator: true },
        {
          label: t("palette.custom.menuDelete"),
          danger: true,
          disabled: running,
          onSelect: () => chipDesign?.deleteDesign(id),
        },
      ],
    });
  };

  // Parts palette (left panel; visibility persists in settings). Any part
  // with a `colors` list (the LED and the segment/bar displays) arms
  // placement directly with the "Default LED color" setting (Settings ▸
  // Appearance) — no placement-time color popover any more. Its color is
  // changed afterward through its own Properties dialog (right-click ▸
  // Properties…); everything else arms its placement ghost directly.
  const palette = new PalettePanel(main, {
    onPickChip: (ref) => {
      if (partDef(ref)?.colors) {
        controller?.armPartPlacement(ref, {
          color: currentSettings.defaultLedColor,
        });
        return;
      }
      // Mains, and an AC adapter's frequency, start at the country's.
      if (ref === "ac") return controller?.armPartPlacement(ref, mainsParamsFor(region)); // prettier-ignore
      if (ref === "adapter") return controller?.armPartPlacement(ref, adapterParamsFor(region)); // prettier-ignore
      controller?.armPartPlacement(ref);
    },
    // The board selector (Full / Half / Tiny + loose strips) lives at the top
    // of the palette; picking one arms board placement, ghost + all.
    onPickBoard: (kit) => controller?.armPlacement(kit),
    // The annotations section (labels + notes) lives at the bottom.
    onPickAnnotation: (kind) => controller?.armAnnotationPlacement(kind),
    onPickSignal: () => controller?.armSignalPlacement(),
    onPickIntegration: (kind) => controller?.armIntegrationPlacement(kind),
    // The CUSTOM folder: design a chip, and a designed chip's own menu.
    onNewCustomChip: () => chipDesign?.newDesign(),
    onCustomChipMenu: (id, x, y) => openCustomChipMenu(id, x, y),
    // The tray's own header chevron / its rail's chevron and section icons.
    // `togglePalette` is declared with the toolbar further below; this closure
    // only runs on a click, long after that.
    onToggle: () => togglePalette(),
    // The tray's own width, dragged on its right edge. Persisted like the open
    // flag — the panel reports, app.js writes.
    width: settings.paletteWidth,
    onWidthChange: (width) => {
      bridge.settings
        .set({ paletteWidth: width })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
    // Collapse state is deliberately NOT persisted — the palette opens with
    // every group shut, every launch (see PalettePanel).
  });
  trayFamilies = () => palette.projectFamilies;
  palette.setVisible(settings.paletteOpen === true);

  // The stage: whichever surface is showing (desk or schematic) with the
  // desktop tab strip over it. It sits to the RIGHT of the palette, so the
  // tabs start where the desk does rather than spanning the palette too.
  const stage = el("div", { class: "app-stage" });
  main.append(stage);
  // The tab strip leads the stage (append order IS the layout) and is ALWAYS
  // showing — with no project open it carries the working desk's own tab, so
  // the "+" that adds another desktop is never hidden behind a project that
  // has to be created first. Its callbacks reach the workspace built below.
  const projectTabs = new ProjectTabs(stage, {
    onSelect: (id) => workspace?.selectTab(id),
    onAdd: () => workspace?.addTab(),
    onImport: () => void workspace?.importTab(),
    onProperties: (id) => workspace?.editTabProperties(id),
    onDuplicate: (id) => void workspace?.duplicateTab(id),
    onExport: (id) => void workspace?.exportTab(id),
    onExportTo: (id, format) => void exportDesktopTo(id, format),
    onDelete: (id) => workspace?.deleteTab(id),
  });
  stage.append(desk);

  // The schematic surface sits beside the desk (Feature 150); a header toggle
  // (or Tab) swaps which one is visible. Constructed further below, once the
  // controller exists to receive its position-nudge commits.
  const schematicViewport = buildSchematicViewport();
  stage.append(schematicViewport);
  let schematicView = null;
  // …and so does the 3D view, built with the schematic once the controller
  // exists (it reads the desk's own LED verdicts through it).
  const view3dViewport = buildView3dViewport();
  stage.append(view3dViewport);
  let view3d = null;
  // The desktop the 3D view last framed (see the workspace's onActiveChange).
  let view3dTab = null;
  let mode = "desk";

  // Build guide (Feature 140): a right-docked panel deriving the BOM / wiring
  // list / assembly steps from the live document. Visibility persists like the
  // palette; the desk-tool pill's BOM segment (⌘B), its own close button, and
  // the native File menu all route through onVisibilityChange so the persisted
  // setting — and the segment's armed state — stay in step however it was
  // flipped.
  let guideBtn = null;
  const buildGuide = new BuildGuide(main, {
    deskDoc,
    // What to BUILD: the wiring, whichever way a switch is set.
    netlist: wiringNetlistCache,
    // The exported BOM file is named after the desktop it was derived from.
    schemaName: () => workspace?.activeTab?.name ?? "desktop",
    onVisibilityChange: (visible) => {
      guideBtn?.classList.toggle("toolbar-btn--active", visible);
      guideBtn?.setAttribute("aria-pressed", String(visible));
      bridge.settings
        .set({ guideOpen: visible })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
  });
  buildGuide.setVisible(settings.guideOpen === true);
  // File ▸ Bill Of Materials… (⌘B) — pushed by the native File menu; the
  // toolbar's own BOM segment calls buildGuide.toggle() directly.
  window.addEventListener("chiphippo:build-guide", () => buildGuide.toggle());

  // Logic analyzer (Feature 210): a bottom-docked waveform panel that records
  // the sim-state stream into timing diagrams. Its channel mutations route
  // through the controller (undo/redo), and it stays live while the sim runs.
  let scopeBtn = null;
  const scopeView = new ScopeView(app, {
    deskDoc,
    netlist: netlistCache,
    height: settings.scopeHeight,
    onVisibilityChange: (visible) => {
      scopeBtn?.classList.toggle("toolbar-btn--active", visible);
      scopeBtn?.setAttribute("aria-pressed", String(visible));
      bridge.settings
        .set({ scopeOpen: visible })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
    onHeightChange: (height) => {
      bridge.settings
        .set({ scopeHeight: height })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
    axisMode: settings.scopeAxis,
    onAxisModeChange: (mode) => {
      bridge.settings
        .set({ scopeAxis: mode })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
    onAddChannel: (kind, ref, opts) =>
      controller?.addScopeChannel(kind, ref, opts),
    onRemoveChannel: (id) => controller?.removeScopeChannel(id),
    onMoveChannel: (id, index) => controller?.moveScopeChannel(id, index),
    onSetGround: (address) => controller?.setScopeGround(address),
  });
  scopeView.setVisible(settings.scopeOpen === true);

  // The transport's last reported mode, kept because several panels need to
  // know the desk is frozen and `sim` itself is built further down.
  let transportMode = "stopped";

  // AI circuit builder (Feature 260): describe a circuit, get a verified design
  // ARMED as a placement ghost. It hands over a design clip and nothing else —
  // the desk's own atomic paste path does the placing, so a generated circuit
  // rides undo/redo exactly as a pasted one does.
  let aiBtn = null;
  let heldDesign = null; // a build finished mid-run, armed at Stop
  // A desktop or project switch stops the run on its way out — and then puts
  // another document on the desk, which cancels any ghost in hand. So a Stop
  // that is a swap's keeps the build, and the desk that arrives is armed with
  // it (it carries its own boards; Escape lets it go).
  let swapping = false;
  window.addEventListener("chiphippo:desk-leaving", () => {
    swapping = transportMode !== "stopped";
  });
  window.addEventListener("chiphippo:desk-loaded", () => {
    swapping = false;
    if (heldDesign && transportMode === "stopped") {
      const clip = heldDesign;
      heldDesign = null;
      controller?.armGeneratedDesign(clip);
    }
  });
  const aiPanel = new AiPanel(app, {
    config: () => currentSettings.ai ?? {},
    height: settings.aiHeight,
    history: settings.aiHistory,
    isLocked: () => transportMode !== "stopped",
    // Review mode (Feature 320) reads the desk: the document through the shared
    // DeskDoc and the nets through the ONE netlist cache, the same pair the
    // build guide and the analyzer take — so a review can never disagree with
    // the BOM or the probe about the same circuit.
    deskDoc,
    netlist: netlistCache,
    // A build that finishes while the circuit runs cannot be placed (the desk
    // is locked); it is kept and armed the moment the run stops, rather than
    // thrown away with the generation the user paid for. `false` tells the
    // panel to say so.
    onDesign: (clip) => {
      if (transportMode !== "stopped") {
        heldDesign = clip;
        return "held";
      }
      heldDesign = null;
      return controller?.armGeneratedDesign(clip);
    },
    onHistoryChange: (entries) => {
      bridge.settings
        .set({ aiHistory: entries })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
    onVisibilityChange: (visible) => {
      aiBtn?.classList.toggle("toolbar-btn--active", visible);
      aiBtn?.setAttribute("aria-pressed", String(visible));
      bridge.settings
        .set({ aiOpen: visible })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
    onHeightChange: (height) => {
      bridge.settings
        .set({ aiHeight: height })
        .catch((err) => console.error("[renderer] settings:set failed:", err));
    },
  });

  // The AI segment is only offered when there is a connection to ask — see
  // createAiReadiness. The button it drives is built with the rest of the
  // desk-tool pill further down, hence the getter.
  const refreshAiReady = createAiReadiness({
    bridge,
    aiPanel,
    getButton: () => aiBtn,
    config: () => currentSettings.ai ?? {},
    remembered: settings.aiOpen === true,
  });
  // Settings ▸ AI changing the provider (a settings patch) or the key itself
  // (which bypasses settings entirely, straight to the OS-encrypted store) are
  // the two ways this answer changes.
  window.addEventListener("chiphippo:ai-key-changed", () => refreshAiReady());

  const deskView = new DeskView(desk, {
    // A plain drag on the empty breadboard is the rubber band, never a pan —
    // panning is Space-drag or the middle button (CLAUDE.md → "Selection").
    panOnEmptyDrag: false,
    camera: settings.viewport,
    onViewportChange: (camera) => {
      zoomControl?.setZoom(camera.zoom);
      controller?.onViewportChange(camera);
      scheduleViewportSave(camera);
    },
  });

  // Everything ON the desk (boards, chips, wires, placement, hover).
  let wireBtn = null;
  let wireDot = null; // the Wire button's color dot: readout AND picker
  let busBtn = null;
  let busWidth = null; // the Bus button's width badge: readout AND picker
  let cutBtn = null; // the veroboard Cut Track tool
  let probeBtn = null;
  let routeBtn = null; // the one-shot Auto-route action
  let fadeBtn = null; // the "Fade wires" toggle
  let modeBtn = null; // the Breadboard ⇄ Schematic segment
  let view3dBtn = null; // the 3D view segment
  let meterBtn = null; // the multimeter segment
  let spiceLiteOn = false; // Settings ▸ Spice Lite (the multimeter's offered)
  let sim = null; // the SimController (created after the toolbar below)
  let memoryBridge = null; // memory-inspector coordinator (created with sim)
  const onWireStateChange = ({ armed, color }) => {
    wireBtn?.classList.toggle("toolbar-btn--active", armed);
    wireBtn?.setAttribute("aria-pressed", String(armed));
    // The Wire button carries a dot showing the active color — and, uniquely
    // among the pill's readouts, it is the picker for it too
    // (components/wire-color-dot.js). Whatever set the color — a swatch, 1–8 —
    // arrives here, so the dot has one repaint path.
    wireDot?.setColor(color);
  };
  const onBusStateChange = ({ armed }) => {
    busBtn?.classList.toggle("toolbar-btn--active", armed);
    busBtn?.setAttribute("aria-pressed", String(armed));
  };
  const onCutStateChange = ({ armed }) => {
    cutBtn?.classList.toggle("toolbar-btn--active", armed);
    cutBtn?.setAttribute("aria-pressed", String(armed));
  };
  const onProbeStateChange = ({ armed }) => {
    probeBtn?.classList.toggle("toolbar-btn--active", armed);
    probeBtn?.setAttribute("aria-pressed", String(armed));
  };
  // Fading the wires is a view preference, so it persists like the panels do —
  // and the button follows whether the toolbar or H flipped it.
  const onWireFadeChange = ({ faded }) => {
    fadeBtn?.classList.toggle("toolbar-btn--active", faded);
    fadeBtn?.setAttribute("aria-pressed", String(faded));
    bridge.settings
      .set({ wiresFaded: faded })
      .catch((err) => console.error("[renderer] settings:set failed:", err));
  };
  controller = new DeskController({
    viewport: desk,
    deskView,
    deskDoc,
    netlist: netlistCache,
    wiringNetlist: wiringNetlistCache,
    onWireStateChange,
    onBusStateChange,
    onCutStateChange,
    // The badge's one repaint path: whatever set the width — a digit key, or
    // the badge's own picker — arrives here.
    onBusNameChange: (name) => busWidth?.setName(name),
    onProbeStateChange,
    onWireFadeChange,
    // Probe context-menu → pin the net as an analyzer channel (and reveal it).
    onAddNetToAnalyzer: (address, opts) => {
      scopeView.addNetChannel(address, opts);
      scopeView.setVisible(true);
    },
    // An edit tried while the circuit runs: one toast says why (its key
    // collapses repeats), with the Stop that gets the desk back.
    onEditRefused: () =>
      notifications.notify({
        key: "edit-while-running",
        variant: "info",
        title: t("desk.locked.title"),
        message: t("desk.locked.message"),
        actionLabel: t("desk.locked.stop"),
        onAction: () => sim?.stop(),
      }),
    onClockToggle: (id) => sim?.manualToggle(id),
    onClockPause: (id) => sim?.toggleClockPause(id),
    // A signal flag selected on the desk lights its button on the rail.
    onSignalSelect: (id) => signalRail?.setSelected(id),
    // …and an Output/Input tag lights its element's card.
    onIntegrationSelect: (id) => integration?.setSelected(id),
    // The built-in Mock first, then this machine's configured connections.
    getConnections: () => knownConnections(currentSettings.serialConnections),
    onOpenSettings: (tab) => openSettings(tab),
    onOpenConnectionWindow: (id) =>
      Promise.resolve(bridge?.serial?.log?.open?.(id)).catch(() => {}),
    // A part dropped from the tray — the capacitor's one-time note.
    onPartPlaced: (ref) => noteCapacitorPlaced(ref),
    // A custom chip's designer/debugger, its breakpoints, and its tab
    // following the selection.
    onOpenChipDesigner: (id) => chipDesign?.openForComponent(id),
    // A CPU's monitor window, which follows the selection while open.
    onOpenCpuMonitor: (id) => cpuMonitor?.open(id),
    // A floated slider's window (a pot's Position), and a Space tapped in
    // it: Run/Stop, refused for the reasons a Space on the desk is.
    sliderBridge: bridge?.slider,
    onSliderRunToggle: () => {
      if (PopupManager.isOpen()) return;
      if (controller.placementArmed || controller.layingInProgress) return;
      sim?.toggle();
    },
    // A custom chip's Properties card edits its DESIGN's part number and
    // description, which the project's chips are the workspace's to change.
    putCustomChip: (chip) =>
      workspace?.putCustomChip(chip) ?? { ok: false, code: "closed" },
    onPartSelect: (sel) => {
      if (sel?.kind !== "part") return;
      chipDesign?.focusComponent(sel.id);
      cpuMonitor?.focusComponent(sel.id);
    },
    chipDebug: chipDebugger,
    // A part's (or a wire's) "Pin Assignment" context-menu item → its
    // floating pin/terminal-assignments OS window (`rows` sizes it to the
    // layout; `rot` is a snapshot of the part's placed rotation — only an
    // oscillator can's corner assignment depends on it, see chip-pinout.js's
    // buildCanPinout; `kind: "wire"` routes main to the query flag pinout.js
    // reads instead of resolving `ref` against the catalog — a wire has no
    // catalog def, so `ref` is just its own id, e.g. "w12").
    //
    // `sheet` is the basename of the part's committed datasheet crop, which
    // main sizes the window against (a diagram wants a wide one). It is the ref
    // for nearly every part and so main could once derive it — but not always:
    // both character-LCD modules show the ONE controller sheet, HD44780. Main
    // has no catalog, so the side that HAS one names the file, exactly as it
    // names `rows`; main still validates it and turns it into the path.
    //
    // A CUSTOM chip is in no catalog the pinout window can load (it is the
    // user's, and the project's copy may differ from the library's), so the
    // side that has it says what the window shows of it — its part number,
    // description, package and pins (`customPinoutOf`). Later changes reach an
    // open window through CustomPinoutSync.
    onOpenPinout: (ref, rows, rot, kind) => {
      const def = partDef(ref);
      return bridge
        .openPinout?.(ref, {
          rows,
          rot,
          kind,
          sheet: datasheetCrop(def),
          chip: def?.custom ? customPinoutOf(def.customChip) : undefined,
        })
        .catch((err) => console.error("[renderer] pinout:open failed:", err));
    },
    // A memory chip's "Inspect memory…" context-menu item → its hex inspector
    // window.
    onOpenMemory: (id) => memoryBridge?.open(id),
    // "Load image… (program)" on a ROM chip → the in-app external programmer.
    onProgramMemory: (id) =>
      void memoryBridge
        ?.program(id)
        .catch((err) =>
          console.error("[renderer] program memory failed:", err),
        ),
    // A ROM chip gets a noise-filled backing file on placement, deleted on
    // removal (the byte store lives in main; Feature 190).
    onCreateMemoryFile: (guid, byteLength) =>
      bridge.mem
        ?.create(guid, byteLength)
        .catch((err) => console.error("[renderer] mem:create failed:", err)),
    onRemoveMemoryFile: (guid) =>
      bridge.mem
        ?.delete(guid)
        .catch((err) => console.error("[renderer] mem:delete failed:", err)),
    // Undo/redo availability drives the native Edit-menu enable state.
    onHistoryChange: (state) =>
      bridge.menu
        ?.setEditState(state)
        .catch((err) =>
          console.error("[renderer] menu:edit-state failed:", err),
        ),
  });

  wireEditMenu(controller);

  const toolbar = document.getElementById("app-toolbar");

  // Three views of the one desk: the breadboard ("desk"), the schematic, and
  // the 3D view. Exactly one is showing. The Schematic segment (and Tab, its key) goes TO the schematic
  // from either other view and back to the breadboard from it, so its icon and
  // label — "where this takes you" — are true from the 3D view too; the 3D
  // segment does the same for the 3D view.
  function setMode(next) {
    mode = next === "schematic" || next === "3d" ? next : "desk";
    const schematic = mode === "schematic";
    const in3d = mode === "3d";
    desk.hidden = mode !== "desk";
    schematicView?.setVisible(schematic);
    view3d?.setVisible(in3d);
    if (view3dBtn) {
      view3dBtn.classList.toggle("toolbar-btn--active", in3d);
      view3dBtn.setAttribute("aria-pressed", String(in3d));
      view3dBtn.setAttribute(
        "aria-label",
        in3d ? t("toolbar.view3d.hide") : t("toolbar.view3d.show"),
      );
      view3dBtn.title = in3d
        ? t("toolbar.view3d.hideTitle")
        : t("toolbar.view3d.showTitle");
    }
    if (!modeBtn) return;
    modeBtn.classList.toggle("toolbar-btn--active", schematic);
    modeBtn.innerHTML = schematic ? BREADBOARD_SVG : SCHEMATIC_SVG;
    modeBtn.setAttribute(
      "aria-label",
      schematic ? t("toolbar.mode.breadboard") : t("toolbar.mode.schematic"),
    );
    modeBtn.title = schematic
      ? t("toolbar.mode.breadboardTitle")
      : t("toolbar.mode.schematicTitle");
    modeBtn.setAttribute("aria-pressed", String(schematic));
  }

  const { pill: filePill, buttons: fileButtons } = buildFilePill(
    () => workspace,
  );

  // DESKTOP actions have no toolbar button at all: Add / Duplicate / Import /
  // Export / Properties / Delete are the application's **Desktop** menu
  // (main.js `buildAppMenu`) and the tab strip's own context menu — a desktop
  // is reached through its tab, so that is where the things one can do to it
  // belong.
  toolbar.append(filePill);

  // The parts tray has no toolbar button: it carries its own chevron in the
  // header and the rail it shuts down to (see PalettePanel), both of which
  // route back here so ⌘P, the chevrons, and the rail's icons are one code path.
  const togglePalette = () => {
    const on = !palette.visible;
    palette.setVisible(on);
    bridge.settings
      .set({ paletteOpen: on })
      .catch((err) => console.error("[renderer] settings:set failed:", err));
  };

  // Which surface the camera actions act on. Fit follows the view you are
  // LOOKING at, like zoom-out-fully does — but it is not a plain camera call on
  // either: the desk's lives on the controller because it RECENTRES the
  // document as well, and the schematic's is the diagram's own (its symbol
  // positions are derived, so there is nothing to move — fitting IS centring).
  const getActiveView = () =>
    mode === "schematic" ? schematicView : mode === "3d" ? view3d : deskView;
  const fitActiveView = () =>
    mode === "schematic"
      ? schematicView.fit()
      : mode === "3d"
        ? view3d.fit()
        : controller.fitToScreen();
  // The same move for a desk that has JUST BEEN LOADED — a project opening, an
  // example desktop landing. The DESK is always recentred and framed, whichever
  // view is showing: centring is a fact about the document. It goes through the
  // controller's load-time form, which keeps the recentre out of undo/redo; the
  // workspace clears the dirty flag straight after, for the same reason —
  // nobody moved the design, the app did, putting it where it can be seen.
  const frameLoadedView = () => {
    controller.fitLoadedDesk();
    if (mode === "schematic") schematicView.fit();
    // A different circuit: the 3D view frames it the next time it draws.
    view3d?.frameNext();
  };

  const deskTools = buildDeskToolPill({
    controller,
    scopeView,
    buildGuide,
    aiPanel,
    setMode,
    getMode: () => mode,
    getActiveView,
    fitActiveView,
    onAutoRoute: (debug) => autoRoute(debug),
  });
  const toolPill = deskTools.pill;
  ({ wire: wireBtn, wireDot, bus: busBtn, busWidth, cut: cutBtn, route: routeBtn, fade: fadeBtn, probe: probeBtn, scope: scopeBtn, meter: meterBtn, guide: guideBtn, mode: modeBtn, view3d: view3dBtn, ai: aiBtn } = deskTools.buttons); // prettier-ignore
  // The multimeter segment is armed while the desk has a meter out —
  // whatever put it there or took it away (the segment, Delete, ⌘Z, a
  // desktop switched to).
  // An action, not a toggle: no pressed state — disabled once MAX_METERS are
  // out, so a fourth is never offered.
  meterBtn.removeAttribute("aria-pressed");
  const syncMeter = () => {
    meterBtn.disabled = controller.meterCount >= MAX_METERS;
    meterBtn.hidden = !spiceLiteOn;
  };
  window.addEventListener("chiphippo:doc-changed", syncMeter);
  window.addEventListener("chiphippo:desk-loaded", syncMeter);
  syncMeter();
  const updateLocateIcon = deskTools.relabelFit;
  toolbar.append(toolPill);
  // The segments that mirror state the app already holds, synced once now that
  // the buttons exist: the controller's wire tool and colour, its wire fading
  // (from the persisted setting), the bus width, and — since `setMode` returns
  // early while `modeBtn` is null — the view toggle.
  onWireStateChange({ armed: false, color: controller.wireColor });
  busWidth.setName(controller.busName);
  controller.setWiresFaded(settings.wiresFaded === true);
  refreshAiReady();
  // Cut track is offered only while the desktop holds a veroboard — there is
  // no copper to cut otherwise. Every edit, undo and desktop swap says
  // doc-changed, so the segment follows the desk; deleting the last veroboard
  // also puts the tool away.
  const showCutTool = () => {
    cutBtn.hidden = !controller.hasVeroboard;
    if (cutBtn.hidden) controller.disarmCutTool();
  };
  window.addEventListener("chiphippo:doc-changed", showCutTool);
  showCutTool();

  // ── Simulation transport (Feature 90/100): Run/Stop, Pause, Step, speed ──
  const notifications = new NotificationStack(document.body);

  /**
   * A capacitor just landed on the board: say, once and out of the way, what
   * one is in this simulator — a value-carrying part that does not filter,
   * smooth or store charge, read by the timing chips and written into a KiCad
   * export. A toast rather than a dialog (nothing waits on it), with "Don't
   * show again" for good; a FUNCTION declaration so the controller's callback,
   * wired above, reaches it. That is the LOGIC engine's capacitor: with Spice
   * Lite on it does filter and store charge, so the note would be wrong and
   * is not shown.
   */
  function noteCapacitorPlaced(ref) {
    if (!partDef(ref)?.capacitor) return;
    if (currentSettings.capacitorNoteDismissed === true) return;
    if (normalizeSpiceConfig(currentSettings.spiceLite).enabled) return;
    notifications.notify({
      key: "capacitor-note",
      variant: "info",
      title: t("desk.capacitorNote.title"),
      message: t("desk.capacitorNote.message"),
      actionLabel: t("desk.capacitorNote.dismiss"),
      onAction: () => {
        currentSettings = { ...currentSettings, capacitorNoteDismissed: true };
        bridge.settings
          .set({ capacitorNoteDismissed: true })
          .catch((err) =>
            console.error("[renderer] settings:set failed:", err),
          );
      },
    });
  }
  exporter = new DesktopExporter({ bridge, notifications });

  // The Arduino serial integration. Its settings writes go straight to the
  // store rather than through the settings-changed event, because the first
  // one can come from the project that BOOTS (a connection it carries that
  // this machine lacks), before that event has a listener.
  integration = createIntegrationShell({
    bridge,
    desk,
    deskDoc,
    notifications,
    getSettings: () => currentSettings,
    setSettings: (patch) => {
      currentSettings = { ...currentSettings, ...patch };
      bridge.settings
        .set(patch)
        .catch((err) => console.error("[renderer] settings:set failed:", err));
      integration?.settingsChanged(patch);
    },
    openSettings,
    getWorkspace: () => workspace,
    getController: () => controller,
  });
  // Generate: after AI, the last of the desk tools — it reads the desk into
  // an artifact, as BOM does, and stays live while the circuit runs.
  toolPill.append(integration.generate);

  // Auto-route (Feature 360). One click, one undo step, and a toast that says
  // what happened — including, when it happens, that some wires were left alone
  // because there was no legal path for them. Silence there would read as a
  // button that did nothing.
  //
  // It ASKS first. One click rewrites every wire on the desktop — hand-placed
  // bends included — and may move a lead to another hole, which is too much for
  // a toolbar segment a stray click can reach, undo or not. The question is
  // skipped only when there is nothing for it to be about (no wire the router
  // would take up), where the run writes nothing anyway.
  //
  // A SELECTION narrows it: only the selected wires move (`routeSelection`),
  // every other one still counted as terrain to route around, and the question
  // says so as `3/9`. A selection naming no wire routes nothing and says how to
  // get what was probably meant — clear it, or select the wires.
  let routing = null; // the AbortController of the run in flight, if any
  const autoRoute = (debug = false) => {
    if (routing) return; // one run at a time; the toast is already saying so
    const total = controller.routableWireCount;
    if (total === 0) {
      void runAutoRoute(debug);
      return;
    }
    const only = controller.routeSelection;
    if (only?.size === 0) {
      PopupManager.choose({
        title: t("desk.autoRouteNothingTitle"),
        message: t("desk.autoRouteNothingMessage", { total }),
        note: t("desk.autoRouteNothingNote"),
        cancelLabel: t("common.ok"),
      });
      return;
    }
    PopupManager.confirm({
      title: t("desk.autoRouteConfirmTitle"),
      message: only
        ? t("desk.autoRouteConfirmSelected", { count: only.size, total })
        : t("desk.autoRouteConfirmMessage", { count: total }),
      note: t(
        only
          ? "desk.autoRouteConfirmSelectedNote"
          : "desk.autoRouteConfirmNote",
      ),
      confirmLabel: t("desk.autoRouteConfirm"),
      onConfirm: () => void runAutoRoute(debug, only),
    });
  };
  const runAutoRoute = async (debug, only = null) => {
    if (routing) return;
    const abort = new AbortController();
    routing = abort;
    // Say so BEFORE starting, and keep saying it. Routing a large desk is
    // seconds of computation, and an app that stops repainting with no
    // explanation reads as one that has crashed. The toast carries the Cancel
    // button, which is why it is not dismissible: a stray click on it would
    // otherwise throw away the only way to stop the run. It shares its key with
    // the outcome, so the result replaces the notice rather than stacking under
    // it, and re-notifying that key rewrites the words in place.
    const say = (message) =>
      notifications.notify({
        key: "auto-route",
        title: t("toolbar.route.label"),
        message,
        sticky: true,
        dismissible: false,
        actionLabel: t("common.cancel"),
        onAction: () => abort.abort(),
      });
    say(t("desk.autoRouteWorking"));
    await new Promise((resolve) => setTimeout(resolve, 32)); // let it paint
    let result = null;
    let failed = false;
    try {
      result = await controller.autoRouteWires({
        debug,
        only,
        signal: abort.signal,
        onProgress: (p) =>
          say(
            p.phase === "tidy"
              ? t("desk.autoRouteTidying", { pass: p.round, passes: p.rounds })
              : t("desk.autoRouteRound", {
                  round: p.round,
                  done: p.done,
                  total: p.total,
                }),
          ),
      });
    } catch (err) {
      // `applyRoutes` refused the plan and rolled itself back, so the desk is
      // exactly as it was — which is the same thing a stale plan means, and is
      // what the user is told. The reason still goes to the console: a refusal
      // here is either a race with a live edit or a bug, and only one of those
      // is worth reading a message about.
      console.error("[renderer] auto-route failed:", err);
      failed = true;
    } finally {
      routing = null;
      notifications.dismiss("auto-route");
    }
    // Nothing was written and there is nothing to say: the circuit is running,
    // or the desk holds no wire the router could take an interest in.
    if (!failed && !result) return;
    let message;
    let variant = "info";
    if (failed || result.stale) message = t("desk.autoRouteStale");
    else if (result.cancelled) message = t("desk.autoRouteCancelled");
    else {
      variant = result.unrouted > 0 ? "warning" : "info";
      message = [
        result.unrouted > 0
          ? t("desk.autoRoutePartial", {
              count: result.routed,
              failed: result.unrouted,
            })
          : t("desk.autoRouteDone", { count: result.routed }),
        // The one thing the router did that is not a drawing, said out loud: a
        // moved lead changes what you plug in, so it is reported rather than
        // left to be noticed.
        result.moved > 0 && t("desk.autoRouteMoved", { count: result.moved }),
      ]
        .filter(Boolean)
        .join(" ");
    }
    notifications.notify({
      key: "auto-route",
      variant,
      title: t("toolbar.route.label"),
      message,
    });
  };

  // Surface net-name merge conflicts (Feature 120) as toasts — a name that
  // loses a merge is reported, never silently dropped.
  new NetNameMonitor(netlistCache, notifications, () => deskDoc.netNameCount);

  // Say the moment a wire puts an AC source straight onto a logic input —
  // before anything runs (model/ac-wiring.js).
  new AcWiringMonitor(
    netlistCache,
    notifications,
    () => ({ components: deskDoc.components }),
    (id) => {
      const comp = deskDoc.getComponent(id);
      if (!comp) return id;
      // A chip by its marking, a brick (mains, an adapter, a transformer) by
      // its title in the user's language.
      const def = partDef(comp.ref);
      const name = def && def.kind !== "chip" ? partTitle(def) : chipMarking(def, comp.ref); // prettier-ignore
      return `${name} (${id})`;
    },
  );

  // Auto-update: the always-on surface for whatever the updater is doing. It
  // is the only one that survives Settings being closed, which is why it lives
  // here and not in the About panel's own listeners.
  new UpdaterMonitor(notifications);

  const { pill: transportPill, buttons: transportButtons } = buildTransportPill(
    () => sim,
  );
  const {
    run: runBtn,
    pause: pauseBtn,
    step: stepBtn,
    speed: speedBtn,
  } = transportButtons;
  toolbar.append(transportPill);
  // Every pill is in: from here the header keeps itself inside the window.
  watchHeaderFit(toolbar.closest(".app-header"), toolbar, toolPill);
  window.addEventListener("chiphippo:sim-state", (e) =>
    showSpeed(speedBtn, sim?.speed ?? 1, e.detail.behind ?? null),
  );
  // The speakers, heard (Spice Lite): each plays its tone at the run's speed.
  const speakerAudio = new SpeakerAudio();
  window.addEventListener("chiphippo:sim-state", (e) =>
    speakerAudio.update(e.detail, sim?.speed ?? 1),
  );

  // Buttons that edit topology are disabled while the circuit runs; the probe,
  // the file actions, and the transport controls stay live. Listed by element
  // rather than queried — the File pill's segments look exactly like the tool
  // pill's, and opening or saving a document is not a topology edit. The parts
  // tray isn't in the list either: showing or hiding a panel edits nothing, and
  // `#enterPlacement` already refuses every pick while the circuit runs (⌘P has
  // always stayed live for the same reason).
  const editButtons = [wireBtn, busBtn, cutBtn, routeBtn];
  const onTransportChange = (mode) => {
    const starting = transportMode === "stopped" && mode !== "stopped";
    transportMode = mode;
    const stopped = mode === "stopped";
    if (starting) noteBrokenCustomChips();
    controller.setEditingLocked(!stopped);
    workspace?.setEditingLocked(!stopped);
    if (stopped) setTransportFace(runBtn, "▶", t("toolbar.transport.run"));
    else setTransportFace(runBtn, "■", t("toolbar.transport.stop"));
    runBtn.title = stopped
      ? t("toolbar.transport.runTitle", { mod: MOD_KEY })
      : t("toolbar.transport.stopTitle", { mod: MOD_KEY });
    runBtn.setAttribute("aria-pressed", String(!stopped));
    runBtn.classList.toggle("toolbar-btn--running", !stopped);
    if (mode === "paused")
      setTransportFace(pauseBtn, "▶", t("toolbar.transport.resume"));
    else setTransportFace(pauseBtn, "⏸", t("toolbar.transport.pause"));
    for (const btn of [pauseBtn, stepBtn, speedBtn]) btn.hidden = stopped;
    for (const btn of editButtons) btn.disabled = !stopped;
    // The third release path for a held signal key (keyup and blur are the
    // other two): the levels are gone anyway, but a stale held set must not
    // leak into the next Run. `releaseSignalKeys` is installed by
    // bindShortcuts, which runs after this closure is built.
    if (stopped) releaseSignalKeys?.();
    if (stopped && swapping)
      swapping = false; // desk-loaded arms it
    else if (stopped && heldDesign) {
      const clip = heldDesign;
      heldDesign = null;
      controller.armGeneratedDesign(clip);
    }
  };
  // A designed chip whose package or Verilog has a problem still seats and
  // is powered, but drives nothing — Run says so, once, naming each.
  const noteBrokenCustomChips = () => {
    const broken = new Set();
    for (const comp of deskDoc.components) {
      const def = partDef(comp.ref);
      if (def?.custom && !def.customRuntime) broken.add(chipMarking(def));
    }
    if (!broken.size) return;
    notifications.notify({
      key: "custom-chip-broken",
      variant: "warning",
      title: t("chipdesign.brokenTitle"),
      message: t("chipdesign.brokenMessage", {
        names: [...broken].join(", "),
        count: broken.size,
      }),
    });
  };
  // The simulation runs on its own Worker thread where it can, on this one
  // where it must (an Arduino on the desk, a custom chip being debugged —
  // components/sim-host.js); the app talks to it the same either way.
  sim = new SimHost({
    deskDoc,
    netlist: netlistCache,
    notifications,
    onTransportChange,
    // The settle-boundary collaborator: the serial link to real Arduinos.
    integration: integration.controller,
    // The chip debugger, which may hold a tick to replay it pass by pass.
    debug: chipDebugger,
  });
  integration.controller.setSim(sim);
  chipDebugger.setSim(sim);

  // Projects & tabbed desktops: owns the open project — the document — which
  // desktop is on the desk, and the swap (with its camera and undo history)
  // through the controller's load path, plus every action behind the File and
  // Desktop menus. Built here because it needs the sim to stop across a
  // switch.
  //
  // A loaded project's padlock goes onto the desk in two places: the wheel
  // itself, and the button that shows it (once it exists — the workspace boots
  // before the padlock is built).
  const applyWheelLock = (locked) => {
    deskView.setWheelLocked(locked);
    view3d?.setWheelLocked(locked);
    deskLock?.setLocked(locked);
  };
  workspace = new ProjectWorkspace({
    bridge,
    deskDoc,
    controller,
    sim,
    tabs: projectTabs,
    getCamera: () => deskView.camera,
    setCamera: (camera) => deskView.setCamera(camera),
    fitView: frameLoadedView,
    boot: projectBoot,
    // A browser tab is left without asking the app (see WEB_AUTO_SAVE_MS).
    autoSaveMs: isWeb() ? WEB_AUTO_SAVE_MS : undefined,
    onActiveChange: (tab) => {
      updateTitle();
      integration?.refresh();
      // Another desktop is another circuit — the 3D view frames it afresh.
      // Only on a SWITCH: this also fires for a save, a rename or the padlock,
      // none of which should throw away where the user has orbited to. (A
      // whole project arriving is framed by `frameLoadedView`.)
      if (tab?.id !== view3dTab) {
        view3dTab = tab?.id ?? null;
        view3d?.frameNext();
      }
    },
    onWheelLock: applyWheelLock,
    // The tray shows every logic family the open project uses, whatever its
    // mode (Feature 400) — re-derived from scratch for each project.
    onProjectAdopted: (docs) =>
      palette.resetProjectFamilies(familiesUsed(docs)),
    // A NEW project starts on the breadboard: there is nothing yet to look at
    // in 3D, and the first thing anyone does with one is place a board. (Open
    // leaves the view alone — that project has something to look at.)
    onNewProject: () => {
      if (mode === "3d") setMode("desk");
    },
    // A project FILE carries the serial connections its elements use (never a
    // port), and a project arriving brings any this machine lacks.
    projectConnections: (docs, previous) =>
      integration.projectConnections(docs, previous),
    onConnections: (list) => integration.mergeProjectConnections(list),
  });
  updateTitle(); // the booted project names the window

  // The chip designer window's host side (custom chips).
  chipDesign = new ChipDesignBridge({
    bridge,
    workspace: () => workspace,
    chipDebug: chipDebugger,
    notifications,
  });
  // The CPU monitor window's host side: which CPU it shows, and its board.
  cpuMonitor = new CpuMonitorBridge({
    bridge,
    deskDoc,
    sim,
    notifications,
    // Its "keep edits" box: the ROMs edited during a run are saved at Stop
    // through the memory inspector's Save.
    keepEdits: settings.cpuMonitorKeepEdits === true,
    onKeepEdits: (on) =>
      bridge.settings
        .set({ cpuMonitorKeepEdits: on })
        .catch((err) => console.error("[renderer] settings:set failed:", err)),
    saveRom: (compId, bytes) =>
      memoryBridge?.keepRunEdits(compId, bytes) ?? Promise.resolve(false),
    // Its breakpoints live in the project, per desktop.
    store: {
      load: () => workspace?.cpuBreakpoints() ?? {},
      save: (map) => workspace?.setCpuBreakpoints(map),
    },
  });
  // A designed chip added, changed or gone: the tray shows the new set, and an
  // open pin-assignments window shows the chip as it now is.
  window.addEventListener("chiphippo:custom-chips-changed", () =>
    palette.refreshCustomChips(),
  );
  new CustomPinoutSync({ bridge, chips: workspace.customChips });

  // Memory-inspector coordinator (Feature 190): bridges inspector windows to the
  // document, the controller (programmer + undo/redo), and the running image.
  memoryBridge = new MemoryBridge({
    deskDoc,
    sim,
    controller,
    bridge,
    notifications,
    // A ROM's bytes travel IN the project file, but they live in a sidecar no
    // signature comparison can see — and re-saving an already-programmed chip
    // leaves the document identical. So the write says so itself.
    onImagesChanged: () => workspace?.markImagesChanged(),
    // Every ROM write flags its chip in the desktop it BEGAN on — wherever
    // that is by the time it lands (on the desk, in the project, or both),
    // never whatever chip shares its id on a desk loaded since.
    whereNow: () => workspace?.deskWhere() ?? null,
    markKept: (where, compId, binding, guid) =>
      workspace?.markRomKept(where, compId, binding, guid),
  });

  // The empty-desk hint disappears once the desk has boards.
  const hint = desk.querySelector(".desk-hint");
  const updateHint = () => {
    hint.hidden = deskDoc.boards.length > 0;
  };
  updateHint();
  window.addEventListener("chiphippo:doc-changed", updateHint);

  zoomControl = new ZoomControl(desk, {
    onZoomIn: (opts) => deskView.zoomIn(opts),
    onZoomOut: (opts) => deskView.zoomOut(opts),
    onReset: () => deskView.resetZoom(),
  });
  zoomControl.setZoom(deskView.camera.zoom);

  // The desk padlock (top-right): shut, the mouse wheel stops moving the desk
  // (DeskView.setWheelLocked says why it locks the wheel and nothing else). It
  // is part of the PROJECT and saved in its file: a toggle is recorded there as
  // an edit, and a project that loads brings its own state back through
  // `applyWheelLock` — the padlock's silent `setLocked`, so a load is never
  // mistaken for a click.
  deskLock = new DeskLock(desk, {
    mod: MOD_KEY,
    onChange: (locked) => {
      deskView.setWheelLocked(locked);
      view3d?.setWheelLocked(locked);
      workspace?.setWheelLocked(locked);
    },
  });
  // The workspace was built first, so the booted project's padlock is applied
  // to the desk already but not yet to the button.
  applyWheelLock(workspace?.wheelLocked === true);

  // External signals (Feature 370): the buttons pinned down the viewport's
  // right edge, between the padlock above and the zoom cluster below. A
  // viewport sibling, so it neither pans nor zooms.
  signalRail = new SignalRail(desk, deskDoc, {
    onPress: (id, on) => sim?.pressSignal(id, on),
    onFlagPointerDown: (id, e) => controller?.beginSignalFlagDrag(id, e),
    // The rail's own right-click opens the SAME menu the flag does — an
    // unplaced signal has no flag, so this is its only route to Remove.
    onContextMenu: (id, e) =>
      controller?.openSignalMenu(id, e.clientX, e.clientY),
    // Dragging a button up or down the rail — only while editing, when a
    // button has nothing to press.
    onReorder: (id, index) => controller?.moveSignal(id, index),
    canReorder: () => Boolean(controller) && !controller.editingLocked,
  });
  // The serial integration's element cards, under the signal buttons in the
  // same column.
  integration.mountRail(signalRail);
  integration.refresh();
  // The palette's SIGNALS row goes disabled once every digit key has a signal
  // (its Output/Input rows once the rail holds all the elements it can, and
  // its Data row all the data buses).
  const refreshSignalsFull = () => {
    palette.setSignalsFull(deskDoc.signals.length >= MAX_SIGNALS);
    palette.setIntegrationsFull(controller.integrationsFull);
    palette.setDataFull(controller.dataFull);
  };
  window.addEventListener("chiphippo:doc-changed", refreshSignalsFull);
  refreshSignalsFull();
  // A family that arrives on the desk mid-session (a paste, an import, an
  // example, an AI build) joins the families the tray keeps showing — sticky
  // until the next project, so a folder never vanishes under the user.
  window.addEventListener("chiphippo:doc-changed", () =>
    palette.noteProjectFamilies(
      familiesUsed([{ components: deskDoc.components }]),
    ),
  );

  // The derived schematic (Feature 150): the same document as chip symbols +
  // routed nets. Symbol nudges and the auto-layout reset commit through the
  // controller so they ride the one undo/redo seam. It draws from the netlist
  // partitioned by WIRING alone (the L4 verifier's `bridges: false` argument):
  // a schematic draws what the build CONNECTED, and the conducting netlist
  // would merge a closed switch's input net into its rail — redrawing the
  // whole input stage as power stubs whenever a switch is left thrown, and
  // moving its pull resistors into the chip's column. (The probe shows the
  // same partition, so a net it highlights is a net drawn here.)
  schematicView = new SchematicView(schematicViewport, {
    doc: deskDoc,
    netlist: wiringNetlistCache,
    onSetSchematicPos: (id, x, y) => controller.setSchematicPos(id, x, y),
    onAutoLayout: () => controller.autoLayoutSchematic(),
  });
  // The 3D view: the same document, stood up. It lights its LEDs and segments — and smokes its burnt parts — from
  // the desk's OWN verdicts, read through the controller, so the two views can never disagree about a lamp.
  view3d = new Desk3DView(view3dViewport, {
    doc: deskDoc,
    ledOf: (id) => controller.ledOf(id),
    segmentOf: (id, seg) => controller.segmentOf(id, seg),
    smokeOf: (id) => controller.smokeOf(id),
    glowing: () => controller.lampsGlowing,
  });
  view3d.setWheelLocked(workspace?.wheelLocked === true);
  setMode("desk"); // sync the initial toggle state

  releaseSignalKeys = bindShortcuts(
    controller,
    sim,
    scopeView,
    togglePalette,
    getActiveView,
    fitActiveView,
    () => setMode(mode === "schematic" ? "desk" : "schematic"),
    () => deskLock.toggle(),
    // ⇧⌘O drops the recent-projects menu under the Open segment — the same
    // place a secondary click on it would, so the key and the pointer put the
    // card in one spot rather than two.
    () => {
      const r = fileButtons.open.getBoundingClientRect();
      void workspace?.openRecentMenu(r.left, r.bottom);
    },
    // ⌥⌘=/−/0. Dispatches the SAME event the Settings dialog emits rather than
    // writing the property itself, so persistence, the live apply and main's
    // fan-out to the other windows all stay on the one seam. A step that
    // saturates changes nothing and says nothing: at 11 or 18 the press is
    // simply a no-op, by the user's own choice of silent feedback.
    (step) => {
      const now = normalizeFontSize(currentSettings.fontSize);
      const next =
        step === "reset"
          ? DEFAULT_FONT_SIZE
          : stepFontSize(now, step === "in" ? 1 : -1);
      if (next === now) return;
      window.dispatchEvent(
        new CustomEvent("chiphippo:settings-changed", {
          detail: { fontSize: next },
        }),
      );
    },
    // Space held pans both 2D views (whichever is showing takes the drag);
    // the tap test asks both whether the hold was used.
    {
      setHeld: (on) => {
        deskView.setSpaceHeld(on);
        schematicView?.setSpaceHeld(on);
      },
      takePanned: () => {
        const desk = deskView.takeSpacePanned();
        return schematicView?.takeSpacePanned() || desk;
      },
    },
  );

  // Changing the language, in place — see createRelabeller.
  const relabelChrome = createRelabeller({
    header,
    desk,
    hint,
    schematicViewport,
    view3dViewport,
    filePill,
    fileButtons,
    toolPill,
    transportPill,
    buttons: { wireBtn, busBtn, cutBtn, routeBtn, fadeBtn, probeBtn, scopeBtn, meterBtn, guideBtn, aiBtn, stepBtn, speedBtn, pauseBtn }, // prettier-ignore
    panels: [
      palette,
      projectTabs,
      buildGuide,
      scopeView,
      aiPanel,
      () => zoomControl,
      () => deskLock,
      () => signalRail,
      () => integration,
      () => schematicView,
      () => view3d,
      () => noticeBanner,
    ],
    // Relabel functions the app ALREADY had, for their own reasons: each
    // owns a control whose label depends on state, so re-running it with the
    // state unchanged IS a relabel.
    restate: [
      updateLocateIcon, // Fit / Zoom-out-fully, whichever it is showing
      () => setMode(mode), // Breadboard ⇄ Schematic, whichever it would go to
      refreshAiReady, // the description, or the reason the segment is off
      () => onTransportChange(transportMode), // Run/Stop, Pause/Resume
      updateTitle,
      // The tool pill's two readouts, whose tooltips name their value.
      () => wireDot?.setColor(controller.wireColor),
      () => busWidth?.setName(controller.busName),
    ],
  });

  // ── Settings (About / Settings dialogs + live application) ────────────────
  // The Settings dialog is deliberately dumb: it broadcasts a patch, and this
  // is where the app persists it (settings.set) and applies it live. Keep the
  // running settings so the dialog opens seeded with the current values.
  // `theme` is deliberately absent from applySettings: main acts on it (it
  // becomes nativeTheme.themeSource), so persisting the patch below IS
  // applying it — for every window at once, not just this one.
  const applySettings = (s) => {
    // The layout a NEW wire gets. Like the default LED colour it is read at
    // placement time, so this only has to keep the controller's copy current.
    controller?.setDefaultWireLayout(s.defaultWireLayout);
    // …and so is the look a multimeter taken out wears (the last one picked
    // in a meter's Properties ▸ Look).
    controller?.setDefaultMeterLook(s.meterLook);
    // Whether opening a tray section closes the rest. Acts on the NEXT one
    // opened, so keeping the panel's copy current is the whole application.
    palette.setAutoClose(s.paletteAutoClose === true);
    // Which logic family the tray shows (Feature 400) — rebuilt at once.
    palette.setFamilyMode(s.logicFamily);
    // Which engine the next Run ticks with (Settings ▸ Spice Lite). Read at
    // Run, so keeping the controller's copy current is the whole application.
    sim?.setSpiceLite(s.spiceLite);
    // …and whether a part's Spice-only Properties (an inductor's Winding) are
    // offered — read when a card opens.
    controller?.setSpiceLite(s.spiceLite);
    // …and whether the tray marks the discretes as limited (the digital
    // engine's caveat; Spice Lite simulates them) — rebuilt at once.
    palette.setSpiceLite(s.spiceLite);
    // …and whether the toolbar offers the multimeter: only Spice Lite has
    // voltages to read (a meter already out is put away from its own menu).
    spiceLiteOn = normalizeSpiceConfig(s.spiceLite).enabled;
    if (meterBtn) meterBtn.hidden = !spiceLiteOn;
    // Whether the toolbar offers the 3D view (Settings ▸ Appearance ▸ 3D
    // enabled; absent or anything but `true` is Off). Off HIDES the segment,
    // and leaves the 3D view if it is showing — its own segment is the way
    // back to the breadboard, and that has just gone.
    const view3dOn = s.view3dEnabled === true;
    if (view3dBtn) view3dBtn.hidden = !view3dOn;
    if (!view3dOn && mode === "3d") setMode("desk");
    // The base of the type scale. This window applies it itself — main fans the
    // same value out to the three auxiliary windows, which have no settings UI
    // and only ever follow.
    applyFontSize(s.fontSize);
    const root = document.documentElement;
    if (s.selectionColor) {
      root.style.setProperty("--color-selection", s.selectionColor);
    } else {
      root.style.removeProperty("--color-selection");
    }
  };
  applySettings(currentSettings);

  window.addEventListener("chiphippo:settings-changed", (e) => {
    const prevLocale = currentSettings.locale ?? "system";
    currentSettings = { ...currentSettings, ...e.detail };
    applySettings(currentSettings);
    // A different provider has a different key, and a base URL can be typed
    // into something unreachable — either changes whether the AI segment has
    // a connection to offer.
    if (e.detail && "ai" in e.detail) refreshAiReady();
    // A connection renamed, re-ported or re-framed: the cards name it, and
    // its serial settings are part of the header's design hash.
    integration?.settingsChanged(e.detail);
    const write = bridge.settings
      .set(e.detail)
      .catch((err) => console.error("[renderer] settings:set failed:", err));
    // A language change is the one setting that needs re-reading rather than
    // just applying: main resolves the catalog FROM the stored preference, so
    // the write has to land before the reload asks for it. It rebuilds its own
    // menu bar off the same write; this window relabels what is on screen.
    if (e.detail && "locale" in e.detail && e.detail.locale !== prevLocale) {
      void write.then(async () => {
        await i18n.init();
        relabelChrome();
        window.dispatchEvent(new CustomEvent("chiphippo:locale-changed"));
      });
    }
  });
  // About / Settings / Keyboard Shortcuts are registered at the TOP of init()
  // (see the note there), so that the application menu answers even when
  // something below it has failed to build. The app version is no longer
  // shown in the header — it lives in the About dialog (the (i) toggle),
  // which fetches it over the IPC bridge.
}

// A FILE OR LINK DROPPED ON THE WINDOW IS NOT A NAVIGATION. Chromium's
// default is to load whatever is dropped; main's navigation guard refuses it,
// but only after the load has begun (and in the web build nothing refuses it
// — the tab would leave the app). Nothing in the app takes a drop, so the
// default is declined for the whole document, before it starts.
for (const type of ["dragover", "drop"]) {
  document.addEventListener(type, (e) => e.preventDefault());
}

// A THROWN init() USED TO BE SILENT, and silence is the worst thing it can
// be: index.html carries no markup of its own, so a boot that died partway
// left a blank (or half-built) window and a menu bar whose items did nothing,
// with no record anywhere of why. The rejected build could not have said what
// went wrong even if someone had been watching.
//
// EXPORTED so a test can wait for the boot it just started. Importing this
// module IS starting the app — there is no second entry point and nothing here
// is called twice — so `booted` is the handle on the run already under way,
// which is what app-boot.test.js awaits before asserting the shell is up. The
// app itself never reads it; index.html loads this file for its side effect.
export const booted = init().catch((err) =>
  console.error("[renderer] app failed to start:", err),
);
